<template lang="html">
  <v-content>
    <!-- Dialog: Add -->
    <AddTodoDialog
      :opened.sync="addTodoDialogOpened"
      @todo-added="refreshTable"
    />

    <!-- Dialog: Send Custom Status -->
    <SendCustomStatusChangeDialog
      v-if="newCustomStatus"
      :opened.sync="sendCustomStatusChangeDialogOpened"
      :selectedQuestionnaires="selectedQuestionnaires"
      :new-custom-status="newCustomStatus"
    />

    <!-- Dialog: Select Custom Status -->
    <SelectCustomStatusDialog
      :opened.sync="selectFirmCustomStatusDialogOpened"
      :selected-questionnaires="selectedQuestionnaires"
      @select-custom-status="selectCustomStatus"
    />

    <!-- Dialog: Upload Tax Return -->
    <UploadTaxReturnDialog
      :opened.sync="uploadTaxReturnDeliveryDialogOpened"
      @refresh-table="refreshTaxReturnsWithPolling"
    />

    <!-- Dialog: Import Tax Return -->
    <ImportTaxReturnDialog
      :opened.sync="importTaxReturnDialogOpened"
      :hideEntitySelect="false"
      @refresh-table="refreshTaxReturnsWithPolling"
    />

    <!-- Dialog: Select Tax Return Method -->
    <SelectTaxReturnMethodDialog
      :opened.sync="selectTaxReturnMethodDialogOpened"
      @open-upload-tax-return-dialog="openUploadTaxReturnDialog"
      @open-import-tax-return-dialog="importTaxReturnDialogOpened = true"
      @refresh-table="refreshTaxReturnsWithPolling"
    />

    <!-- Dialog: Upload Signature Request -->
    <UploadSignatureRequestDialog
      :opened.sync="uploadSignatureRequestDialogOpened"
      @refresh-table="refreshSignatureRequestsWithPolling"
    />

    <!-- Dialog: Notification Subscription -->
    <NotificationSubscriptionDialog
      :opened.sync="notificationSubscriptionDialogOpened"
      :selected-entity-ids="selectedEntityIds"
      @refresh="$store.dispatch('companies/getCompanies')"
    />

    <!-- Dialog: Select Assignment -->
    <v-dialog
      v-model="selectAssignmentDialogOpened"
      width="600"
      @keydown.esc="selectAssignmentDialogOpened = false"
    >
      <v-card>
        <v-card-title class="title"
          >Update Assignments for selected Clients ({{
            selectedClients.length
          }})</v-card-title
        >
        <v-card-text>
          <v-layout column v-if="!isEmpty(firmUsers)">
            <div class="text-xs-left">Assigned Firm User</div>
            <v-autocomplete
              placeholder="Select firm user"
              solo
              v-model="selectedAssignedAdvisorId"
              :items="firmUsers"
              item-value="id"
              item-text="name"
              class="default-input"
            />
          </v-layout>

          <v-layout column v-if="!isEmpty(firmUsers) && useFirmRoles">
            <div class="text-xs-left">Assigned Preparer</div>
            <v-autocomplete
              placeholder="Select Preparer"
              solo
              v-model="selectedCpaId"
              :items="firmUsers"
              item-value="id"
              item-text="name"
              class="default-input"
            />
          </v-layout>

          <v-layout column v-if="!isEmpty(firmUsers) && useFirmRoles">
            <div class="text-xs-left">Assigned Client Manager</div>
            <v-autocomplete
              placeholder="Select Client Manager"
              solo
              v-model="selectedClientManagerId"
              :items="firmUsers"
              item-value="id"
              item-text="name"
              class="default-input"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="default-input primary text-none"
            @click="updateAssignments()"
            :loading="assignmentLoading"
          >
            Save
          </v-btn>
          <v-btn
            outline
            class="default-input error--text text-none"
            @click="selectAssignmentDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Lock Questionnaire After Export Confirmation -->
    <v-dialog
      v-model="lockQuestionnaireAfterExportConfirmationDialogOpened"
      width="600"
      @keydown.esc="
        lockQuestionnaireAfterExportConfirmationDialogOpened = false
      "
    >
      <v-card block flat v-if="selectedQuestionnaire">
        <v-card-title class="title pb-0"> Confirmation </v-card-title>
        <v-card-text class="text-xs-left subheading">
          <v-alert type="error" :value="true" outline>
            Do you want to
            {{
              selectedQuestionnaire.track_changes
                ? ''
                : 'track changes for this questionnaire or '
            }}lock this questionnaire after exporting?
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary--text text-none"
            outline
            v-if="!selectedQuestionnaire.track_changes"
            @click="toggleTrackChanges(true)"
          >
            Track Changes
          </v-btn>
          <v-btn
            class="primary text-none"
            @click="toggleLocked(selectedQuestionnaire)"
          >
            Lock Questionnaire
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="
              lockQuestionnaireAfterExportConfirmationDialogOpened = false
            "
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Select Entity -->
    <v-dialog
      v-model="selectEntityDialogOpened"
      width="600"
      persistent
      @keydown.esc="selectEntityDialogOpened = false"
    >
      <v-card block flat v-if="selectedClient">
        <v-card-title class="title">Select Entity</v-card-title>
        <v-card-text>
          <v-layout column>
            <div class="text-xs-left">Entity Name</div>
            <v-select
              hide-details
              placeholder="Select an Entity"
              solo
              v-model="selectedEntity"
              :items="selectedClient.companies"
              return-object
              item-text="name"
              flat
              class="default-input mb-4"
              @change="
                selectEntityDialogOpened = false;
                selectEntityToEdit
                  ? openEditEntityDialog(selectedEntity)
                  : (removeEntityConfirmationDialogOpened = true);
              "
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer class="hidden-xs-only" />
          <v-btn
            :block="$vuetify.breakpoint.xsOnly"
            outline
            class="default-input error--text text-capitalize"
            @click="selectEntityDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Uload Dialog -->
    <v-dialog
      width="600"
      persistent
      v-model="uploadDialogOpened"
      @keydown.esc="uploadDialogOpened = false"
    >
      <v-card flat v-if="selectedQuestion">
        <v-card-title class="title">
          {{ $t('documents.uploadDocuments') }}
        </v-card-title>
        <v-card-text>
          <v-alert
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="primary"
            class="text-xs-left mb-4"
            v-if="!isEmpty(selectedQuestion.options)"
          >
            {{ $t('alerts.dragAndDropFiles2') }}
          </v-alert>
          <v-alert
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="primary"
            class="text-xs-left mb-4"
            v-else
          >
            {{ $t('alerts.dragAndDropFiles3') }}
          </v-alert>
          <v-layout
            column
            class="mb-4"
            v-if="!isEmpty(selectedQuestion.options)"
          >
            <div class="grey--text text--darken-1 text-xs-left mr-4">
              Document Option{{
                selectedQuestion.options.length > 1 ? 's' : ''
              }}
            </div>
            <v-layout align-center class="mb-4">
              <v-select
                v-model="selectedDocumentType"
                :items="selectedQuestion.options"
                placeholder="Select Document Option"
                class="default-input"
                solo
                hide-details
              />
              <v-btn
                :disabled="!selectedDocumentType"
                class="default-input primary text-capitalize hidden-xs-only"
              >
                {{ $t('documents.selectFiles') }}
              </v-btn>
            </v-layout>
            <v-btn
              :disabled="!selectedDocumentType"
              class="default-input primary text-capitalize hidden-sm-and-up mb-4"
            >
              {{ $t('documents.selectFiles') }}
            </v-btn>
          </v-layout>
          <v-layout column class="mb-4" v-else>
            <v-btn class="default-input primary text-capitalize mb-4">
              {{ $t('documents.selectFiles') }}
            </v-btn>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer class="hidden-xs-only" />
          <v-btn
            :block="$vuetify.breakpoint.xsOnly"
            class="default-input primary text-capitalize"
            disabled
          >
            {{ $t('documents.uploadAll') }}
          </v-btn>
          <v-btn
            :block="$vuetify.breakpoint.xsOnly"
            outline
            class="default-input error--text text-capitalize"
            @click="uploadDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Overview Options -->
    <v-dialog
      v-model="overviewOptionsDialogOpened"
      @keydown.esc="overviewOptionsDialogOpened = false"
      scrollable
      width="500"
    >
      <v-card v-if="currentUser.overview_setting">
        <v-card-title class="title"> Options </v-card-title>
        <v-card-text class="subheading darkgrey--text">
          <v-layout column class="text-xs-left pa-2">
            <v-layout align-center class="mb-4">
              <v-flex xs11 class="pr-4"
                >Initially show <b>All Clients</b>
                <p class="caption">
                  The Overview page shows
                  <b
                    >All
                    {{ projectViewEnabled ? 'Projects' : 'Questionnaires' }}</b
                  >
                  by default. Toggle this option if you want to see
                  <b>All Clients</b> as default
                </p></v-flex
              >
              <v-spacer />
              <v-flex>
                <v-switch
                  hide-details
                  class="pt-0 mt-0"
                  v-model="currentUser.overview_setting.show_clients_first"
                />
              </v-flex>
            </v-layout>
            <v-layout align-center class="mb-4">
              <v-flex xs11 class="pr-4"
                >Hide Locked
                <p class="caption">
                  Hide
                  {{ projectViewEnabled ? 'projects' : 'questionnaires' }} that
                  are marked locked from the Overview page
                </p></v-flex
              >
              <v-spacer />
              <v-flex>
                <v-switch
                  hide-details
                  class="pt-0 mt-0"
                  v-model="currentUser.overview_setting.hide_locked"
                />
              </v-flex>
            </v-layout>
            <v-layout align-center class="mb-4">
              <v-flex xs11 class="pr-4"
                >Hide Completed
                <p class="caption">
                  Hide completed
                  {{ projectViewEnabled ? 'projects' : 'questionnaires' }} from
                  the Overview page
                </p></v-flex
              >
              <v-spacer />
              <v-flex>
                <v-switch
                  hide-details
                  class="pt-0 mt-0"
                  v-model="currentUser.overview_setting.hide_completed"
                />
              </v-flex>
            </v-layout>
            <v-layout
              align-center
              class="mb-4"
              v-if="currentUser.firm_access !== 'basic'"
            >
              <v-flex xs11 class="pr-4"
                >Show only assigned clients
                <p class="caption">
                  This hides clients and their entities that you are not
                  assigned to from the Overview page
                </p></v-flex
              >
              <v-spacer />
              <v-flex>
                <v-switch
                  hide-details
                  class="pt-0 mt-0"
                  v-model="currentUser.overview_setting.show_assigned_clients"
                />
              </v-flex>
            </v-layout>
            <v-layout align-center class="mb-4">
              <v-flex xs11 class="pr-4"
                >Use Client Tags
                <p class="caption">
                  Manage and view tags for each client
                </p></v-flex
              >
              <v-spacer />
              <v-flex>
                <v-switch
                  hide-details
                  class="pt-0 mt-0"
                  v-model="currentUser.overview_setting.use_contact_tags"
                />
              </v-flex>
            </v-layout>
            <v-layout align-center class="mb-4">
              <v-flex xs11 class="pr-4"
                >Use Entity Tags
                <p class="caption">
                  Manage and view tags for each entity
                </p></v-flex
              >
              <v-spacer />
              <v-flex>
                <v-switch
                  hide-details
                  class="pt-0 mt-0"
                  v-model="currentUser.overview_setting.use_entity_tags"
                />
              </v-flex>
            </v-layout>
            <v-layout align-center class="mb-4">
              <v-flex xs11 class="pr-4"
                >Show reminder date
                <p class="caption">
                  Shows the next reminder date for each questionnaire.
                </p></v-flex
              >
              <v-spacer />
              <v-flex>
                <v-switch
                  hide-details
                  class="pt-0 mt-0"
                  v-model="currentUser.overview_setting.show_next_reminder_date"
                />
              </v-flex>
            </v-layout>
            <v-layout align-center class="mb-4">
              <v-flex xs11 class="mr-2"
                >Filter by Sender
                <p class="caption">
                  Introduces a new option to filter Questionnaires based on the
                  Sender.
                </p></v-flex
              >
              <v-flex>
                <v-switch
                  hide-details
                  class="pt-0 mt-0"
                  v-model="currentUser.overview_setting.filter_by_sender"
                />
              </v-flex>
            </v-layout>
            <v-layout align-center class="mb-4">
              <v-flex xs8 class="mr-2"
                >Sort Rule
                <p class="caption">
                  Controls how you want to see the overview table by default.
                  Column filters won&apos;t be affected.
                </p></v-flex
              >
              <v-flex xs5 class="ml-2">
                <v-select
                  v-model="currentUser.overview_setting.sort_rule"
                  :items="sortRuleOptions"
                  item-text="text"
                  item-value="value"
                  hide-details
                  class="default-input"
                  solo
                />
              </v-flex>
            </v-layout>
            <v-layout align-center class="mb-4">
              <v-flex xs8 class="mr-2"
                >Default Year
                <p class="caption">
                  Default filter for
                  {{ projectViewEnabled ? 'projects' : 'questionnaires' }} on
                  the Overview page
                </p></v-flex
              >
              <v-flex xs4 class="ml-2">
                <v-select
                  v-model="currentUser.overview_setting.default_year"
                  :items="mappedYearOptionsForDefaultYear"
                  item-text="text"
                  item-value="value"
                  hide-details
                  class="default-input"
                  solo
                />
              </v-flex>
            </v-layout>
            <v-layout align-center row class="mb-4">
              <v-layout row wrap>
                <v-flex xs12 class="pr-4">
                  Default Page sizes
                  <p class="caption mb-1">
                    Initial page size for Overview tables
                  </p>
                </v-flex>
                <v-flex xs5>
                  <v-select
                    :items="settingsPaginationOptions"
                    v-model="
                      currentUser.overview_setting.all_questionnaires_page_size
                    "
                    label="All Questionnaires"
                  />
                </v-flex>
                <v-spacer />
                <v-flex xs5 offset-xs1>
                  <v-select
                    :items="settingsPaginationOptions"
                    v-model="currentUser.overview_setting.all_clients_page_size"
                    label="All Clients"
                  />
                </v-flex>
              </v-layout>
            </v-layout>

            <v-layout align-center class="mb-4" v-if="externalUserIdAllowed">
              <v-flex xs11 class="pr-4"
                >Show <b>External User ID</b>
                <p class="caption">
                  The Overview page shows <b>external entity ID</b> by default.
                  Toggle this option if you want to show
                  <b>external user ID</b> as default
                </p></v-flex
              >
              <v-spacer />
              <v-flex>
                <v-switch
                  hide-details
                  class="pt-0 mt-0"
                  v-model="currentUser.overview_setting.show_external_user_id"
                />
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="primary text-capitalize" @click="saveOverviewSetting()">
            apply
          </v-btn>
          <v-btn
            class="error--text text-capitalize"
            outline
            @click="overviewOptionsDialogOpened = false"
          >
            cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: User Import Dialog -->
    <ClientImportDialog
      :opened.sync="userImportDialogOpened"
      :firm-users="firmUsers"
    />

    <!-- Dialog: Questionnaire -->
    <v-dialog
      v-model="questionnaireDialogOpened"
      max-width="1200"
      persistent
      :fullscreen="$vuetify.breakpoint.smAndDown || fullscreenDialog"
      @keydown.esc="questionnaireDialogOpened = false"
    >
      <v-card v-if="selectedQuestionnaire">
        <v-card-title class="mb-2 pb-0">
          <v-layout align-center v-if="$vuetify.breakpoint.smAndUp">
            <div class="title mr-2">
              {{ selectedEntityName }} -
              {{ selectedQuestionnaire.formatted_name
              }}{{
                selectedQuestionnaire.formatted_name.includes(
                  selectedQuestionnaire.tax_year,
                )
                  ? ''
                  : ` (${selectedQuestionnaire.tax_year})`
              }}
              <v-tooltip bottom v-if="selectedQuestionnaire.locked">
                <v-icon slot="activator" class="mx-1 darkgrey--text" size="22"
                  >lock</v-icon
                >
                <span>Locked from further edits</span>
              </v-tooltip>
              <v-tooltip bottom v-if="selectedQuestionnaire.track_changes">
                <v-chip slot="activator" class="mx-1" small
                  >Tracking Changes</v-chip
                >
                <span
                  >All activities in the questionnaire will notified until
                  completion.</span
                >
              </v-tooltip>
              <v-chip class="mx-1" small v-if="selectedQuestionnaire.is_draft"
                >Draft</v-chip
              >
            </div>
            <v-spacer />
            <soraban-action-button
              v-if="!selectedQuestionnaire.is_draft"
              @click="
                generateQuestionnaireLink(
                  selectedQuestionnaire.id,
                  selectedQuestionnaire.company_id,
                )
              "
              icon="copy"
              help="Copy Link"
            />
            <soraban-action-button
              @click="sendOpenItemsDialogOpened = true"
              icon="email"
              :help="
                selectedQuestionnaire.locked
                  ? 'Unlock the questionnaire before requesting additional items'
                  : 'Request Additional Items'
              "
              :disabled="selectedQuestionnaire.locked"
            />
            <soraban-action-button
              @click="viewParentQuestionnaire()"
              icon="person"
              help="View as Client"
            />
            <soraban-action-button
              v-if="!selectedQuestionnaire.is_draft"
              @click="
                selectedQuestionnaire.locked
                  ? toggleLocked(selectedQuestionnaire)
                  : (lockQuestionnaireConfirmationDialogOpened = true)
              "
              :icon="selectedQuestionnaire.locked ? 'lock_open' : 'lock'"
              :help="selectedQuestionnaire.locked ? 'Unlock' : 'Lock'"
            />
            <soraban-action-button
              slot="activator"
              icon="get_app"
              help="Export"
              @click="openQuestionnaireExportDialog(selectedQuestionnaire)"
            />
            <v-tooltip bottom class="mr-1">
              <v-btn
                slot="activator"
                fab
                small
                flat
                :class="fullscreenDialog ? 'primary--text' : ''"
                class="white text-capitalize ma-0 ml-1"
                @click="fullscreenDialog = !fullscreenDialog"
              >
                <v-icon size="25"> fullscreen </v-icon>
              </v-btn>
              <span>Fullscreen</span>
            </v-tooltip>
            <v-btn
              fab
              small
              flat
              class="white error--text text-capitalize ma-0"
              @click="closeQuestionnaireDialog()"
            >
              <v-icon size="25"> clear </v-icon>
            </v-btn>
          </v-layout>

          <v-layout align-center v-else>
            <div class="title mr-2" style="text-align: left">
              {{ selectedQuestionnaire.formatted_name }}
              <v-tooltip bottom v-if="selectedQuestionnaire.locked">
                <v-icon slot="activator" class="mr-1 darkgrey--text" size="22"
                  >lock</v-icon
                >
                <span>Locked from further edits</span>
              </v-tooltip>
              <v-tooltip bottom v-if="selectedQuestionnaire.track_changes">
                <v-chip slot="activator" class="mx-1" small
                  >Tracking Changes</v-chip
                >
                <span
                  >All activities in the questionnaire will notified until
                  completion.</span
                >
              </v-tooltip>
              <v-chip class="mx-1" small v-if="selectedQuestionnaire.is_draft"
                >Draft</v-chip
              >
            </div>

            <v-spacer />
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn fab small flat class="ma-0" v-on="on">
                  <v-icon size="25"> more_horiz </v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-tile
                  @click="
                    selectedQuestionnaire.locked
                      ? toggleLocked(selectedQuestionnaire)
                      : (lockQuestionnaireConfirmationDialogOpened = true)
                  "
                >
                  <v-list-tile-title>{{
                    selectedQuestionnaire.locked ? 'Unlock' : 'Lock'
                  }}</v-list-tile-title>
                </v-list-tile>

                <v-list-tile
                  @click="openQuestionnaireExportDialog(selectedQuestionnaire)"
                >
                  <v-list-tile-title>Export</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
            <v-btn
              fab
              small
              flat
              class="white error--text text-capitalize ma-0"
              @click="questionnaireDialogOpened = false"
            >
              <v-icon size="25"> clear </v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>

        <v-card-text
          v-if="
            !questionnaireLoading &&
              selectedQuestionnaire.questionnaire_questions
          "
          class="py-0"
        >
          <SmartQuestionnaireView
            :current-questionnaire="selectedQuestionnaire"
            :current-company="getEntity(selectedQuestionnaire)"
            @refresh-current-questionnaire="refreshQuestionnaireDialog"
            @refresh-questionnaires="refreshQuestionnaires"
            @close-questionnaire-dialog="closeQuestionnaireDialog"
          />
        </v-card-text>
        <v-card-text v-else class="py-5">
          <v-progress-circular
            indeterminate
            size="25"
            color="primary"
          ></v-progress-circular>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog: Add Entity -->
    <v-dialog
      v-model="addEntityDialogOpened"
      width="800"
      persistent
      @keydown.esc="addEntityDialogOpened = false"
    >
      <v-card block flat v-if="selectedClient">
        <v-card-title>
          <span class="title"
            >Add New Entity for {{ selectedClient.tax_return_name }}</span
          >
          <v-spacer />
          <v-switch
            class="default-input my-0 py-0"
            solo
            hide-details
            label="Detailed Attributes"
            v-model="advancedClient"
          />
        </v-card-title>
        <v-card-text>
          <v-layout wrap class="text-xs-left">
            <v-flex xs6 v-if="newType === 'Individual'" class="px-2">
              <div>First Name</div>
              <v-text-field
                name="first-name"
                placeholder="Jack"
                type="text"
                v-model="newClientFirstName"
                required
                :rules="rules.presence"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs6 v-if="newType === 'Individual'" class="px-2">
              <div>Last Name</div>
              <v-text-field
                name="last-name"
                placeholder="Smith"
                type="text"
                v-model="newClientLastName"
                required
                :rules="rules.presence"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs6 class="px-2">
              <div>Entity Type</div>
              <v-combobox
                v-model="newType"
                :items="newTypes"
                placeholder="Select the entity"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs6 class="px-2" v-if="newType !== 'Individual'">
              <div>Entity Name</div>
              <v-text-field
                name="company-name"
                placeholder="e.g. VentureScalers"
                type="text"
                v-model="newCompanyName"
                required
                :rules="duplicateExistenceRules"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs6 class="px-2" v-else>
              <div>Entity Name</div>
              <v-text-field
                name="company-name"
                type="text"
                disabled
                :error-messages="
                  duplicateRules(newClientLastName + ', ' + newClientFirstName)
                "
                :value="newClientLastName + ', ' + newClientFirstName"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs4 class="px-2" v-if="advancedClient">
              <div>
                {{ newType === 'Individual' ? 'Work' : 'Business' }} Email
              </div>
              <v-text-field
                placeholder="Optional"
                v-model="newEntityBusinessEmail"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex
              xs4
              class="px-2"
              v-if="advancedClient && newType !== 'Individual'"
            >
              <div>Other Email</div>
              <v-text-field
                placeholder="Optional"
                v-model="newEntityOtherEmail"
                solo
                class="default-input"
              />
            </v-flex>
            <v-flex xs4 class="px-2" v-if="advancedClient">
              <div>Street Address</div>
              <v-text-field
                solo
                label="Street Address"
                v-model="newEntityStreet"
                class="default-input"
              />
            </v-flex>
          </v-layout>
          <v-layout class="text-xs-left" v-if="advancedClient">
            <v-flex xs4 class="px-2">
              <div>Country</div>
              <v-autocomplete
                solo
                label="Country"
                :items="otherCountries"
                v-model="newEntityCountry"
                class="default-input"
              />
            </v-flex>
            <v-flex xs4 class="px-2">
              <div>City</div>
              <v-text-field
                solo
                label="City"
                v-model="newEntityCity"
                class="default-input"
              />
            </v-flex>
            <v-flex xs4 class="px-2" v-if="advancedClient">
              <div>
                {{ isEmpty(newEntityCountry) ? 'State' : 'Province/State' }}
              </div>
              <v-autocomplete
                solo
                hide-selected
                label="State"
                v-model="newEntityState"
                class="default-input"
                :items="states"
              />
            </v-flex>
            <v-flex xs4 class="px-2" v-if="advancedClient">
              <div>
                {{ isEmpty(newEntityCountry) ? 'Zip Code' : 'Postal Code' }}
              </div>
              <v-text-field
                solo
                label="Zip"
                v-model="newEntityZip"
                class="default-input"
                mask="#####"
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            :disabled="!newEntityFilledOut"
            @click="addEntity()"
          >
            Add Entity
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="addEntityDialogOpened = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Edit Due Date -->
    <v-dialog
      v-model="editDueDateDialogOpened"
      width="400"
      persistent
      @keydown.esc="editDueDateDialogOpened = false"
    >
      <v-card block flat v-if="selectedQuestionnaire">
        <v-card-title>
          <span class="title">Edit Reminders</span>
          <v-spacer />
        </v-card-title>
        <v-card-text>
          <v-layout column>
            <v-layout class="text-xs-left">
              <v-flex xs12 class="pr-2">
                <div>
                  Due Date<v-tooltip top
                    ><v-icon slot="activator" size="15" class="ml-1"
                      >info</v-icon
                    ><span
                      >Reminders will be sent out automatically to incomplete
                      questionnaire approaching the due date</span
                    ></v-tooltip
                  >
                </div>
                <v-text-field
                  class="default-input body-1 m-0"
                  type="date"
                  solo
                  item-text="name"
                  item-value="id"
                  :rules="rules.dueDateRules"
                  v-model="selectedQuestionnaire.due_date"
                />
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-switch
            label="Disable Reminders"
            v-model="remindersDisabled"
            hide-details
            :disabled="savingQuestionnaire"
            @change="saveRemindersDisabled"
            class="default-input ma-0 pa-0"
          />
          <v-spacer />
          <v-btn
            class="primary text-none"
            :disabled="!questionnaireSavable || savingQuestionnaire"
            :loading="savingQuestionnaire"
            @click="saveQuestionnaire()"
          >
            Save
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="editDueDateDialogOpened = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Edit Entity -->
    <EditEntityDialog
      v-model="editEntityInfoDialogOpened"
      :selected-entity="selectedEntity"
      :selected-user="selectedClient"
      :is-individual-entity="
        selectedEntity && oneIndividualEntitiesExist(selectedEntity)
      "
      :firm-contact-tags="
        firmContactTagsAndSelf(selectedEntity ? selectedEntity.user : null)
      "
      :firm-entity-tags="
        firmEntityTagsAndSelf(selectedEntity ? selectedEntity : null)
      "
      :new-types="newTypes"
      :firm-users="firmUsers"
      @loading="tableLoading = $event"
      @refresh-questionnaires="refreshQuestionnaires()"
      @refresh="refreshTable()"
    />

    <!-- Dialog: Edit Client -->
    <EditClientDialog
      :opened.sync="editClientInfoDialogOpened"
      :selected-client="selectedClient"
      @refresh-clients="refreshTable()"
      @refresh-questionnaires="refreshQuestionnaires()"
      :firm-contact-tags="firmContactTagsAndSelf(selectedClient)"
      :firm-users="firmUsers"
    />

    <!-- Dialog: Create Client -->
    <CreateClientDialog
      v-model="addClientDialogOpened"
      :advanced-client="advancedClient"
      :new-types="newTypes"
      :firm-users="firmUsers"
    />

    <!-- Dialog: Scheduler -->
    <template-scheduler-dialog
      v-model="templateSchedulerDialogOpened"
      @create-template-scheduler="createTemplateScheduler()"
    />

    <!-- Dialog: Send Questionnaire -->
    <send-questionnaire-dialog
      v-model="sendQuestionnaireDialogOpened"
      @open-send-template-dialog="
        sendTemplateDialogOpened = true;
        newTemplateScheduler = false;
      "
      @open-send-open-items-dialog="createCustomQuestionnaire()"
      @open-send-document-drop-off-dialog="
        sendDocumentDropOffDialogOpened = true
      "
    />

    <!-- Dialog: Send Template -->
    <send-template-dialog
      v-model="sendTemplateDialogOpened"
      multiple
      :selected-entity-ids="selectedEntityIds"
      @refresh-questionnaires="refreshQuestionnaires()"
      :questionnaire-loading="tableLoading"
      :new-template-scheduler="newTemplateScheduler"
    />

    <SendDocumentDropOffDialog
      v-model="sendDocumentDropOffDialogOpened"
      multiple
      :selected-entity-ids="selectedEntityIds"
      @refresh-questionnaires="refreshQuestionnaires()"
      :questionnaire-loading="tableLoading"
    />

    <!-- Dialog: Send Open Items -->
    <send-open-items-dialog
      v-model="sendOpenItemsDialogOpened"
      multiple
      :selected-entity-ids="selectedEntityIds"
      @refresh-questionnaires="refreshQuestionnaires()"
      :questionnaire-loading="tableLoading"
      :parent-questionnaire="selectedQuestionnaire"
      :questionnaire-dialog-opened="questionnaireDialogOpened"
      :open-item-questionnaire="selectedOpenItemQuestionnaire"
      @refresh-questionnaire-dialog="refreshQuestionnaireDialog"
    />

    <!-- Dialog: Export Questionnaire -->
    <ExportQuestionnaireDialog
      v-model="exportQuestionnaireDialogOpened"
      :selected-questionnaires="selectedQuestionnairesForExport"
      :all-questionnaires="allQuestionnaires"
      @open-lock-questionnaire-confirmation-dialog="
        openLockQuestionnaireConfirmationDialog()
      "
      :currentCompany="getEntity(selectedQuestionnairesForExport[0])"
    />

    <!-- Dialog: Questionnaire Reminder -->
    <send-questionnaire-reminder-dialog
      :opened.sync="sendQuestionnaireReminderDialogOpened"
      :selected-questionnaires="selectedQuestionnaires"
      :multiple="!isEmpty(selectedQuestionnaires)"
      @refresh-questionnaires="refreshQuestionnaires()"
    />

    <!-- Dialog: Edit Due Date -->
    <EditDueDateDialog
      :opened.sync="editBulkDueDateDialogOpened"
      :selected-questionnaires="selectedQuestionnaires"
      @refresh-questionnaires="refreshQuestionnaires()"
    />

    <!-- Dialog: Disable Enable Reminder -->
    <DisableEnableRemindersDialog
      :opened.sync="disableRemindersDialogOpened"
      :selected-questionnaires="selectedQuestionnaires"
      @refresh-questionnaires="refreshQuestionnaires()"
    />

    <!-- Dialog: Bulk Update Tags -->
    <BulkUpdateTagsDialog
      :opened.sync="bulkUpdateTagsDialogOpened"
      :selected-clients="selectedClients"
      :selected-entities="selectedEntities"
      @refresh-clients="refreshClients()"
      :selected-view="clientView"
    />

    <!-- Dialog: Manage Tags -->
    <ManageTagsDialog
      :opened.sync="manageTagsDialogOpened"
      :tagType.sync="manageTagType"
      @refresh-firm="getCurrentUserFirm"
    />

    <!-- Confirm: Remove Questionnaire -->
    <ConfirmationDialog
      v-model="removeQuestionnaireConfirmationDialogOpened"
      @confirm="deleteQuestionnaire"
    >
      Are you sure you want to remove the questionnaire? You will remove all
      progress done on the questionnaire so far, and the client will not be able
      to access or see the deleted questionnaire. The client will not be
      notified.
    </ConfirmationDialog>

    <!-- Confirm: Lock Questionnaire -->
    <ConfirmationDialog
      v-model="lockQuestionnaireConfirmationDialogOpened"
      @confirm="toggleLocked(selectedQuestionnaire)"
    >
      Are you sure you want to lock this questionnaire? Locking prevents any
      additional edits to the questionnaire.
    </ConfirmationDialog>

    <!-- Confirm: Start Export -->
    <!-- <ConfirmationDialog
      v-model="startExportConfirmationDialogOpened"
      @confirm="startExport"
    >
      Are you sure you want to start data entry? You cannot access the tax
      software until you cancel the export.
    </ConfirmationDialog> -->

    <!-- Confirm: Send Client Login -->
    <ConfirmationDialog
      v-model="sendClientLoginInfoDialogOpened"
      @confirm="sendClientLoginInfo"
    >
      Are you sure you want to send sign-in link to
      {{ selectedClients.length }} client{{
        selectedClients.length > 1 ? 's' : ''
      }}? Their passwords will be reset.
    </ConfirmationDialog>

    <!-- Confirm: Remove Client -->
    <ConfirmationDialog
      v-model="removeClientConfirmationDialogOpened"
      @confirm="deleteClient"
    >
      <template v-if="selectedClient">
        Are you sure you want to remove {{ selectedClient.tax_return_name }}?
        All Documents and information for this client will be deleted.
      </template>
    </ConfirmationDialog>

    <!-- Confirm: Remove Entity -->
    <ConfirmationDialog
      v-model="removeEntityConfirmationDialogOpened"
      @confirm="deleteEntity"
    >
      <template v-if="selectedEntity">
        Are you sure you want to remove {{ selectedEntity.name
        }}{{
          isIndividual(selectedEntity)
            ? ''
            : ` (${selectedEntity.user.tax_return_name})`
        }}? All Documents and information for {{ selectedEntity.name }} will be
        deleted.
      </template>
    </ConfirmationDialog>

    <!-- Confirm: Delete Multiple Questionnaire -->
    <ConfirmationDialog
      v-model="deleteMultipleQuestionnairesConfirmationDialogOpened"
      @confirm="deleteMultipleQuestionnaires"
    >
      <template v-if="!isEmpty(selectedQuestionnaires)">
        Are you sure you want to delete
        {{ selectedQuestionnaires.length }} questionnaire{{
          selectedQuestionnaires.length > 0 ? 's' : ''
        }}?
      </template>
    </ConfirmationDialog>

    <!-- Alert: Has Expired Link -->
    <v-alert
      type="error"
      :value="hasExpiredLinkedAccount"
      class="mb-4 text-xs-left text--darken-1"
      style="font-size: 15px"
      outline
    >
      Your email integration was disconnected. Please
      <b
        ><a class="red--text text--darken-1" href="/admin-profile?integrations"
          >click here</a
        ></b
      >
      to reconnect it.
    </v-alert>

    <!-- Partial: Header -->
    <v-layout class="mb-2" align-center>
      <sc-h1>{{ $t('overview.title') }}</sc-h1>
    </v-layout>

    <!-- Partial: Content -->
    <v-card class="content-container">
      <!-- Partial: Tabs -->
      <v-layout class="mx-2 mt-2">
        <v-tabs
          v-model="active"
          color="white"
          slider-color="primary"
          class="mb-2"
          ref="tabs"
        >
          <v-tab
            key="questionnaires"
            id="all-questionnaires-btn"
            ripple
            @click="onOverviewClick"
            class="text-none subheading"
          >
            All {{ projectViewEnabled ? 'Projects' : 'Questionnaires' }}
          </v-tab>
          <v-tab
            key="clients"
            id="all-clients-btn"
            ripple
            @click="onClientsClick"
            class="text-none subheading"
          >
            {{ $t('overview.clientsTab') }}
          </v-tab>
          <v-tab
            :style="
              signatureRequestsEnabled ? 'display:block;' : 'display:none;'
            "
            key="signatures"
            id="all-signature-requests-btn"
            ripple
            @click="onSignatureRequests"
            class="text-none subheading"
          >
            All Signature Requests
          </v-tab>
          <v-tab
            :style="
              newTaxReturnDeliveryEnabled ? 'display:block;' : 'display:none;'
            "
            key="tax-returns"
            id="all-tax-returns-btn"
            ripple
            @click="onTRDClick"
            class="text-none subheading"
          >
            All Tax Return Deliveries
          </v-tab>
          <v-tab
            :style="allActivitiesEnabled ? 'display:block;' : 'display:none;'"
            key="client-activities"
            id="client-activities-btn"
            ripple
            @click="onActivitiesClick"
            class="text-none subheading"
          >
            All Activities
          </v-tab>
          <v-tab
            :style="todoTasksEnabled ? 'display:block;' : 'display:none;'"
            key="todo-tasks"
            id="todo-tasks-btn"
            ripple
            @click="onTasksClick"
            class="text-none subheading"
          >
            All Tasks
          </v-tab>
          <v-tab
            :style="reviewAndExportEnabled ? 'display:block;' : 'display:none;'"
            key="review-export"
            id="review-export-btn"
            ripple
            @click="onReviewExportClick"
            class="text-none subheading"
          >
            Review & Export
          </v-tab>
        </v-tabs>

        <template v-if="$vuetify.breakpoint.smAndUp">
          <v-spacer />

          <!-- Selected Questionnaire Dropdown -->
          <v-menu
            transition="slide-y-transition"
            bottom
            right
            class="my-0"
            v-if="!isEmpty(selectedQuestionnaires) && active === 0"
          >
            <sc-button-group slot="activator">
              <sc-button
                type="ghost"
                :is-disabled="tableLoading"
                :is-icon-only="true"
                :with-tooltip="true"
                icon-left="dots-vertical"
                >Actions for Selected</sc-button
              >
            </sc-button-group>
            <v-list class="caption">
              <v-list-tile
                v-if="!isEmpty(selectedQuestionnairesWithoutDrafts)"
                @click="openSelectedQuestionnairesForExport()"
              >
                <v-list-tile-title>
                  Export Selected ({{
                    selectedQuestionnairesWithoutDrafts.length
                  }})
                </v-list-tile-title>
              </v-list-tile>

              <v-list-tile
                v-if="selectedQuestionnairesThatCanBeReminded.length > 0"
                @click="sendQuestionnaireReminderDialogOpened = true"
              >
                <v-list-tile-title>
                  Send Reminder{{
                    selectedQuestionnairesThatCanBeReminded.length > 1
                      ? 's'
                      : ''
                  }}
                  ({{ selectedQuestionnairesThatCanBeReminded.length }})
                </v-list-tile-title>
              </v-list-tile>

              <v-list-tile @click="editBulkDueDateDialogOpened = true">
                <v-list-tile-title>
                  Edit Due Date{{
                    selectedQuestionnaires.length > 1 ? 's' : ''
                  }}
                  ({{ selectedQuestionnaires.length }})
                </v-list-tile-title>
              </v-list-tile>

              <v-list-tile @click="disableRemindersDialogOpened = true">
                <v-list-tile-title>
                  Edit Reminder{{
                    selectedQuestionnaires.length > 1 ? 's' : ''
                  }}
                  ({{ selectedQuestionnaires.length }})
                </v-list-tile-title>
              </v-list-tile>

              <v-list-tile
                @click="
                  deleteMultipleQuestionnairesConfirmationDialogOpened = true
                "
              >
                <v-list-tile-title>
                  Delete Selected ({{ selectedQuestionnaires.length }})
                </v-list-tile-title>
              </v-list-tile>

              <v-list-tile
                @click="lockMultipleQuestionnaires()"
                v-if="!isEmpty(selectedQuestionnairesThatCanBeLocked)"
              >
                <v-list-tile-title>
                  Lock Selected ({{
                    selectedQuestionnairesThatCanBeLocked.length
                  }})
                </v-list-tile-title>
              </v-list-tile>
              <v-list-tile
                v-if="customStatusesEnabled"
                @click="openChangeMultipleFirmCustomStatusesDialog()"
              >
                <v-list-tile-title>
                  Change Firm-internal Status for Selected ({{
                    selectedQuestionnaires.length
                  }})
                </v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="downloadOverviewPageAsSpreadsheet()">
                <v-list-tile-title>
                  Download Overview Page for Selected Questionnaires ({{
                    selectedQuestionnaires.length
                  }})
                </v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>

          <!-- Selected tasks Dropdown -->
          <v-menu
            transition="slide-y-transition"
            bottom
            right
            class="my-0"
            v-if="!isEmpty(selectedTaskIds) && active === 4"
          >
            <sc-button-group slot="activator">
              <sc-button
                type="ghost"
                :is-disabled="tableLoading"
                icon-left="dots-vertical"
                :is-icon-only="true"
                :with-tooltip="true"
                >Actions for Selected</sc-button
              >
            </sc-button-group>
            <v-list class="caption">
              <v-list-tile
                @click="updateSelectedTasksStatus(taskStatus.Completed)"
              >
                <v-list-tile-title>
                  Mark Selected as Completed
                </v-list-tile-title>
              </v-list-tile>

              <v-list-tile @click="updateSelectedTasksStatus(taskStatus.Todo)">
                <v-list-tile-title> Restore Selected </v-list-tile-title>
              </v-list-tile>

              <v-list-tile
                @click="updateSelectedTasksStatus(taskStatus.Dismissed)"
              >
                <v-list-tile-title> Dismiss Selected </v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>

          <!-- Actions: Questionnaires -->
          <sc-button-group v-if="active === 0">
            <sc-button
              :data-onboarding="isOnboarding && active === 0 ? 'true' : 'false'"
              @click="
                sendQuestionnaireDialogOpened = true;
                selectedEntityIds = [];
              "
              type="ghost"
              icon-left="add"
              :is-icon-only="true"
              :with-tooltip="true"
              >{{
                projectViewEnabled ? 'New Project' : 'New Questionnaire'
              }}</sc-button
            >
            <sc-button
              @click="$router.push('/questionnaire-templates')"
              type="ghost"
              icon-left="file"
              :is-icon-only="true"
              :with-tooltip="true"
              >View Templates</sc-button
            >
            <sc-button
              @click="templateSchedulerDialogOpened = true"
              type="ghost"
              icon-left="calendar"
              :is-icon-only="true"
              :with-tooltip="true"
              >Scheduler</sc-button
            >
          </sc-button-group>

          <!-- Actions: Clients -->
          <sc-button-group v-if="active === 1">
            <sc-button
              :data-onboarding="isOnboarding && active === 1 ? 'true' : 'false'"
              @click="
                currentUser.firm_access === 'basic' ? '' : openAddClientDialog()
              "
              :is-disabled="currentUser.firm_access === 'basic'"
              type="ghost"
              icon-left="add"
              :is-icon-only="true"
              :with-tooltip="true"
              >Add Client</sc-button
            >
            <sc-button
              v-if="clientView === 'entities' && !isEmpty(selectedEntities)"
              @click="openNotificationSubscriptionDialog()"
              type="ghost"
              :is-icon-only="true"
              icon-left="bell"
              :with-tooltip="true"
              >Notification</sc-button
            >
            <sc-button
              v-if="
                currentUser.overview_setting.use_contact_tags &&
                  !isEmpty(selectedClients) &&
                  clientView === 'users'
              "
              type="ghost"
              @click="bulkUpdateTagsDialogOpened = true"
              :is-icon-only="true"
              :with-tooltip="true"
              icon-left="tag"
              >Bulk Update Contact Tags ({{
                selectedClients.length
              }})</sc-button
            >
            <sc-button
              v-if="
                currentUser.overview_setting.use_entity_tags &&
                  !isEmpty(selectedEntities) &&
                  clientView === 'entities'
              "
              type="ghost"
              @click="bulkUpdateTagsDialogOpened = true"
              :is-icon-only="true"
              :with-tooltip="true"
              icon-left="tag"
              >Bulk Update Entity Tags ({{
                selectedEntities.length
              }})</sc-button
            >
            <sc-button
              v-if="
                clientView === 'users' &&
                  !isEmpty(selectedClients) &&
                  currentUser.firm_access !== 'basic'
              "
              @click="selectAssignmentDialogOpened = true"
              type="ghost"
              :is-icon-only="true"
              :with-tooltip="true"
              icon-left="user-circle-gear"
              >Assignments</sc-button
            >
            <sc-button
              v-if="!isEmpty(selectedClients) && clientView === 'users'"
              @click="sendClientLoginInfoDialogOpened = true"
              type="ghost"
              :is-icon-only="true"
              :with-tooltip="true"
              icon-left="send"
              >Send Sign-in Link</sc-button
            >
            <sc-button
              v-if="clientView === 'entities' && !isEmpty(selectedEntities)"
              @click="openSendQuestionnaireDialog()"
              type="ghost"
              :is-icon-only="true"
              :with-tooltip="true"
              icon-left="send"
              >Send Questionnaire</sc-button
            >
            <sc-button
              @click="
                currentUser.firm_access === 'basic'
                  ? ''
                  : (userImportDialogOpened = true)
              "
              :is-disabled="currentUser.firm_access === 'basic'"
              type="ghost"
              icon-left="file"
              :is-icon-only="true"
              :with-tooltip="true"
              >Import Clients</sc-button
            >
          </sc-button-group>

          <!-- Signature Requests -->
          <sc-button-group v-if="active === 2">
            <sc-button
              @click="openUploadSignatureRequestDialog()"
              type="ghost"
              icon-left="upload"
              :is-icon-only="true"
              :with-tooltip="true"
              >Upload Signature Request</sc-button
            >
          </sc-button-group>

          <!-- Actions: TRD -->
          <sc-button-group v-if="active === 3">
            <sc-button
              v-if="importTaxReturnDraftEnabled"
              @click="selectTaxReturnMethodDialogOpened = true"
              type="ghost"
              icon-left="add"
              :is-icon-only="true"
              :with-tooltip="true"
              >Send tax return delivery</sc-button
            >
            <sc-button
              v-else
              @click="openUploadTaxReturnDialog()"
              type="ghost"
              icon-left="upload"
              :is-icon-only="true"
              :with-tooltip="true"
              >Upload Tax Return for Delivery</sc-button
            >
          </sc-button-group>

          <!-- Actions: Tasks -->
          <sc-button-group v-if="active === 5">
            <sc-button
              @click="addTodoDialogOpened = true"
              type="ghost"
              icon-left="add"
              :is-icon-only="true"
              :with-tooltip="true"
              >Add a to-do task</sc-button
            >
          </sc-button-group>

          <!-- Actions: Generic -->
          <sc-button-group>
            <sc-button
              @click="refreshTable()"
              type="ghost"
              icon-left="refresh"
              :is-icon-only="true"
              :with-tooltip="true"
              >Refresh Table</sc-button
            >
            <sc-button
              @click="overviewOptionsDialogOpened = true"
              type="ghost"
              icon-left="gear"
              :is-icon-only="true"
              :with-tooltip="true"
              >Options</sc-button
            >
          </sc-button-group>
        </template>
      </v-layout>

      <!-- Questionnaires -->
      <v-card-text v-if="active === 1">
        <!-- Partial: Table Filters -->
        <v-layout align-center wrap class="mb-2">
          <template v-if="$vuetify.breakpoint.smAndUp">
            <!-- Use contact tags -->
            <v-flex
              xs2
              class="px-2"
              v-if="currentUser.overview_setting.use_contact_tags"
            >
              <div class="text-xs-left">
                Client Tags
                <v-tooltip bottom>
                  <v-icon
                    slot="activator"
                    class="darkgrey--text ml-1"
                    size="15"
                    style="cursor: pointer"
                    @click="
                      manageTagsDialogOpened = true;
                      manageTagType = 'client-tags';
                    "
                  >
                    edit
                  </v-icon>
                  <span>Edit Client Tags for Firm</span>
                </v-tooltip>
              </div>
              <v-select
                multiple
                v-model="selectedClientTagsFilter"
                @change="
                  updateFilterRecord(
                    'selectedClientTagsFilter',
                    selectedClientTagsFilter,
                  )
                "
                :class="
                  getHighlightedClass(
                    'selectedClientTagsFilter',
                    selectedClientTagsFilter,
                  )
                "
                :items="firmContactTagsWithNone"
                chips
                small-chips
                hide-details
                placeholder="Select Tags to Filter"
                solo
                class="default-input"
              />
            </v-flex>

            <!-- Use entity tags -->
            <v-flex
              xs2
              class="px-2"
              v-if="
                currentUser.overview_setting.use_entity_tags &&
                  clientView === 'entities'
              "
            >
              <div class="text-xs-left">
                Entity Tags
                <v-tooltip bottom>
                  <v-icon
                    slot="activator"
                    class="darkgrey--text ml-1"
                    size="15"
                    style="cursor: pointer"
                    @click="
                      manageTagsDialogOpened = true;
                      manageTagType = 'entity-tags';
                    "
                  >
                    edit
                  </v-icon>
                  <span>Edit Entity Tags for Firm</span>
                </v-tooltip>
              </div>
              <v-select
                multiple
                v-model="selectedEntityTagsFilter"
                @change="
                  updateFilterRecord(
                    'selectedEntityTagsFilter',
                    selectedEntityTagsFilter,
                  )
                "
                :class="
                  getHighlightedClass(
                    'selectedEntityTagsFilter',
                    selectedEntityTagsFilter,
                  )
                "
                :items="firmEntityTagsWithNone"
                hide-details
                placeholder="Select Tags to Filter"
                solo
                class="default-input"
              />
            </v-flex>

            <!-- Use entity tags for entities -->
            <v-flex
              :xs1="currentUser.overview_setting.use_entity_tags"
              :xs2="!currentUser.overview_setting.use_entity_tags"
              class="px-2"
              v-if="clientView === 'entities'"
            >
              <div class="text-xs-left">Entity Type</div>
              <v-select
                v-model="entityFilter"
                @change="updateFilterRecord('entityFilter', entityFilter)"
                :class="getHighlightedClass('entityFilter', entityFilter)"
                :items="entitiesWithAll"
                hide-details
                solo
                class="default-input"
              />
            </v-flex>

            <!-- Assigned to -->
            <v-flex xs2 class="px-2" v-if="currentUser.firm_access !== 'basic'">
              <div class="text-xs-left">Assigned To</div>
              <v-select
                v-model="selectedAssignedUser"
                @change="
                  updateFilterRecord(
                    'selectedAssignedUser',
                    selectedAssignedUser,
                  )
                "
                :class="
                  getHighlightedClass(
                    'selectedAssignedUser',
                    selectedAssignedUser,
                  )
                "
                :items="getFirmUsersFilterOptions()"
                item-text="name"
                item-value="id"
                hide-details
                solo
                class="default-input"
              />
            </v-flex>

            <!-- Firm group -->
            <v-flex xs2 v-if="showGroupFilters" class="px-2">
              <div class="text-xs-left">Firm group</div>
              <v-select
                v-model="selectedFirmGroup"
                @change="
                  updateFilterRecord('selectedFirmGroup', selectedFirmGroup)
                "
                :class="
                  getHighlightedClass('selectedFirmGroup', selectedFirmGroup)
                "
                :items="[
                  {
                    name: 'All',
                    id: -1,
                  },
                  ...firmGroups,
                ]"
                item-text="name"
                item-value="id"
                hide-details
                solo
                class="default-input"
              />
            </v-flex>

            <!-- Hide inactive -->
            <v-flex
              :xs1="currentUser.overview_setting.use_contact_tags"
              :xs2="!currentUser.overview_setting.use_contact_tags"
              class="px-2"
            >
              <div class="text-xs-left">Hide inactive</div>
              <v-switch
                v-model="hideInactive"
                @change="updateFilterRecord('hideInactive', hideInactive)"
                hide-details
                solo
                class="default-input my-0 py-0"
              />
            </v-flex>

            <!-- Action: Clear Filters -->
            <sc-button-group v-if="hasStoredFilters">
              <sc-button
                type="secondary"
                class="mt-2"
                size="small"
                @click="clearFilters()"
                icon-left="close"
                >Clear Filters</sc-button
              >
            </sc-button-group>

            <v-spacer />

            <!-- Client view toggle -->
            <v-flex xs2 class="pl-5">
              <v-radio-group
                v-model="clientView"
                class="pa-0 my-0"
                row
                hide-details
              >
                <v-radio
                  label="Entity View"
                  value="entities"
                  class="my-0 pa-0"
                />
                <v-radio label="User View" value="users" />
              </v-radio-group>
            </v-flex>
          </template>

          <v-flex xs12 class="px-2 pt-3">
            <div class="text-xs-left">Client Search</div>
            <v-text-field
              v-model="clientSearch"
              class="default-input subheading"
              solo
              hide-details
              placeholder="Look for entity or client name"
              style="max-width: 400px"
            />
          </v-flex>
        </v-layout>

        <!-- Partial: Table Info -->
        <aside
          v-if="selectedCount > 0"
          class="table-helper"
          :is-between="true"
          :gap="8"
        >
          <sc-button-group>
            <sc-button
              @click="selectAllClientsOrEntities()"
              v-if="someClientsOrEntitiesNotSelected"
              type="ghost"
              size="small"
              icon-left="add"
              >Select All ({{ clientViewCount }})</sc-button
            >
            <sc-label type="info">{{ selectedCount }} selected</sc-label>
          </sc-button-group>
          <sc-button-group>
            <sc-button
              v-if="clientView === 'entities' && !isEmpty(selectedEntities)"
              @click="openSendQuestionnaireDialog()"
              type="primary"
              size="small"
              icon-right="send"
              >Send Questionnaire</sc-button
            >
          </sc-button-group>
        </aside>

        <!-- Client View Table -->
        <v-data-table
          v-if="clientView === 'users' && $vuetify.breakpoint.smAndUp"
          :headers="clientsHeaders"
          :items="usersMapped"
          :search="clientSearch"
          :loading="tableLoading"
          :pagination.sync="clientPagination"
          :rows-per-page-items="clientPagination.options"
          :custom-filter="customUserFilter"
          class="clients-table text-xs-left"
          select-all
          v-model="selectedClients"
        >
          <template slot="headerCell" slot-scope="props">
            <template v-if="props.header.text === 'Assigned to'">
              <v-tooltip bottom>
                <template slot="activator">
                  <v-layout align-center>
                    {{ props.header.text }}
                    <v-icon size="15" class="ml-1"> help_outline </v-icon>
                  </v-layout>
                </template>
                <span>
                  Assigned user in the firm is the contact point for the client.
                </span>
              </v-tooltip>
            </template>
            <template v-else>
              <span>
                {{ props.header.text }}
              </span>
            </template>
          </template>
          <template slot="items" slot-scope="props">
            <tr :key="props.id" slot="header">
              <td>
                <v-checkbox
                  v-model="props.selected"
                  primary
                  color="lightgrey"
                  hide-details
                ></v-checkbox>
              </td>
              <td class="black--text ph-no-capture">
                <template v-if="pageLoading"> Loading </template>
                <template v-else>
                  <template v-if="props.item.companies.length === 1">
                    <v-layout align-center wrap>
                      <a
                        class="client-name mr-1"
                        @click="
                          changeCurrentCompany(
                            $event,
                            props.item.companies[0].id,
                            `/admin-dashboard/${props.item.companies[0].hash_id}`,
                          )
                        "
                        :href="
                          '/admin-dashboard/' + props.item.companies[0].hash_id
                        "
                      >
                        {{ props.item.tax_return_name }}
                      </a>
                      <span
                        v-if="
                          shouldShowExternalUserId
                            ? props.item.external_user_id
                            : props.item.external_entity_id
                        "
                        @click="
                          changeCurrentCompany(
                            $event,
                            props.item.companies[0].id,
                            `/admin-dashboard/${props.item.companies[0].hash_id}`,
                          )
                        "
                        :href="
                          '/admin-dashboard/' + props.item.companies[0].hash_id
                        "
                        style="cursor: pointer"
                      >
                        <i
                          >({{
                            shouldShowExternalUserId
                              ? props.item.external_user_id
                              : props.item.external_entity_id
                          }})
                        </i>
                      </span>
                      <v-tooltip
                        bottom
                        v-if="
                          hasNewMessageForUser(props.item.id) &&
                            props.item.id !== currentUser.id
                        "
                      >
                        <v-icon
                          slot="activator"
                          color="success"
                          @click="
                            changeCurrentCompany(
                              $event,
                              props.item.companies[0].id,
                              `/conversation/${props.item.companies[0].hash_id}`,
                            )
                          "
                          >mail</v-icon
                        >
                        <span>New Message</span>
                      </v-tooltip>
                      <v-chip
                        v-if="!props.item.paying"
                        color="error"
                        small
                        class="white--text"
                        >Inactive</v-chip
                      >
                      <v-chip
                        v-else-if="props.item.readonly_access"
                        color="warning"
                        small
                        class="black--text"
                        >Readonly</v-chip
                      >
                      <v-chip small round v-if="props.item.for_testing"
                        >Test</v-chip
                      >
                      <template
                        v-if="showGroupFilters && props.item.firm_group"
                      >
                        <v-chip small>
                          {{ props.item.firm_group.name }}
                        </v-chip>
                      </template>
                    </v-layout>
                  </template>

                  <v-menu
                    transition="slide-y-transition"
                    bottom
                    nudge-bottom="30"
                    class="my-1"
                    v-else
                  >
                    <v-layout slot="activator" align-center>
                      <span>{{ props.item.tax_return_name }}</span
                      ><v-chip small round class="mx-2"
                        >{{ props.item.companies.length }} Entities</v-chip
                      ><v-chip small round v-if="props.item.for_testing"
                        >Test</v-chip
                      >
                      <v-tooltip
                        bottom
                        v-if="
                          hasNewMessageForUser(props.item.id) &&
                            props.item.id !== currentUser.id
                        "
                      >
                        <v-icon
                          slot="activator"
                          color="success"
                          v-if="
                            hasNewMessageForUser(props.item.id) &&
                              props.item.id !== currentUser.id
                          "
                          >mail</v-icon
                        >
                        <span>New Message</span>
                      </v-tooltip>
                      <v-chip
                        v-if="!props.item.paying"
                        color="error"
                        small
                        class="white--text"
                        >Inactive</v-chip
                      >
                    </v-layout>
                    <v-list>
                      <v-list-tile
                        :key="i"
                        v-for="(company, i) in props.item.companies"
                        @click="
                          changeCurrentCompany(
                            $event,
                            company.id,
                            `/admin-dashboard/${company.hash_id}`,
                          )
                        "
                        :href="'/admin-dashboard/' + company.hash_id"
                      >
                        <v-list-tile-title>
                          {{ company.tax_return_name }} ({{
                            company.entity_type
                          }})<i v-if="company.external_entity_id"
                            >({{ company.external_entity_id }})
                          </i>
                          <v-chip
                            v-if="company.inactive"
                            color="error"
                            small
                            class="white--text inactive-chip"
                            >Inactive</v-chip
                          >
                          <v-chip
                            v-else-if="company.readonly_access"
                            color="warning"
                            small
                            class="black--text readonly-chip"
                            >Readonly</v-chip
                          >
                        </v-list-tile-title>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </template>
              </td>
              <td class="text-xs-left ph-no-capture">
                <template v-if="pageLoading"> Loading </template>
                <template v-if="props.item.has_temp_email">
                  <v-icon size="16" slot="activator" color="orange"
                    >warning</v-icon
                  >
                  User without email
                </template>
                <template v-else>
                  {{ props.item.email }}
                </template>
              </td>
              <td class="text-xs-left ph-no-capture">
                <template v-if="pageLoading"> Loading </template>
                <template v-else>
                  {{
                    props.item.phone_number
                      ? maskPhoneNumber(props.item.phone_number)
                      : '-'
                  }}
                </template>
              </td>

              <!-- Column: Contact Tags -->
              <td
                class="text-xs-left"
                v-if="currentUser.overview_setting.use_contact_tags"
              >
                <v-layout v-if="props.item.contact_tags < 2">
                  <v-chip
                    :key="i"
                    v-for="(tag, i) in props.item.contact_tags"
                    small
                  >
                    {{ tag.name }}
                  </v-chip>
                </v-layout>
                <v-layout v-else>
                  <v-chip
                    :key="i"
                    v-for="(tag, i) in props.item.contact_tags"
                    small
                    v-if="i < 2"
                  >
                    {{ tag.name }}
                  </v-chip>
                  <v-tooltip bottom v-if="props.item.contact_tags.length > 2">
                    <v-chip slot="activator" small>
                      <span class="caption"
                        >+{{ props.item.contact_tags.length - 2 }}</span
                      >
                    </v-chip>
                    <span
                      ><div
                        v-for="(tag, i) in props.item.contact_tags"
                        v-if="i >= 2"
                      >
                        {{ tag.name }}
                      </div></span
                    >
                  </v-tooltip>
                </v-layout>
              </td>

              <!-- Column: Preparer -->
              <td class="text-xs-left ph-no-capture" v-if="useFirmRoles">
                {{ getFirmUserName(props.item.cpa_id) }}
              </td>

              <!-- Column: Client Manager -->
              <td class="text-xs-left ph-no-capture" v-if="useFirmRoles">
                {{ getFirmUserName(props.item.client_manager_id) }}
              </td>

              <!-- Column: Assigned To -->
              <td class="text-xs-left ph-no-capture">
                <v-autocomplete
                  v-if="
                    currentUser.firm_access !== 'basic' || allowAssignInGroup
                  "
                  class="default-input body-1"
                  hide-details
                  solo
                  :items="
                    filterFirmUsers(
                      props.item.assigned_advisor_id,
                      props.item.firm_group,
                    )
                  "
                  :value="props.item.assigned_advisor_id"
                  :key="`user_assigned_${props.item.id}`"
                  placeholder="Not Assigned"
                  item-text="name"
                  item-value="id"
                  @change="
                    newAssignedAdvisorId =>
                      updateAssignment(props.item, newAssignedAdvisorId)
                  "
                />
                <div
                  v-else-if="props.item.assigned_advisor_id && firmUsers.length"
                >
                  {{
                    firmUsers.find(a => a.id === props.item.assigned_advisor_id)
                      .name
                  }}
                </div>
                <div v-else class="grey--text text--darken-1">-</div>
              </td>

              <td>
                <v-layout align-center>
                  <v-menu
                    transition="slide-y-transition"
                    bottom
                    left
                    nudge-bottom="30"
                    class="my-1"
                  >
                    <v-icon slot="activator" color="#0a1a25">
                      more_vert
                    </v-icon>
                    <v-list>
                      <v-list-tile @click="openEditClientInfo(props.item)">
                        <v-list-tile-title>
                          Edit Client Information
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        @click="openEditEntityDialog(props.item.companies[0])"
                        v-if="props.item.companies.length === 1"
                      >
                        <v-list-tile-title>
                          Edit Entity Info
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        @click="openSelectEntityDialogToEdit(props.item)"
                        v-if="props.item.companies.length > 1"
                      >
                        <v-list-tile-title>
                          Edit Entity Info
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        @click="openAddEntityDialog(props.item)"
                        v-if="currentUser.firm_access !== 'basic'"
                      >
                        <v-list-tile-title> Add Entity </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        @click="openSelectEntityDialogToDelete(props.item)"
                        v-if="
                          props.item.companies.length > 1 &&
                            currentUser.firm_access !== 'basic'
                        "
                      >
                        <v-list-tile-title> Remove Entity </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        @click="sendClientSignInLink(props.item.id)"
                        v-if="props.item.paying"
                      >
                        <v-list-tile-title>
                          Send Sign-in Link
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        @click="openRemoveClientConfirmationDialog(props.item)"
                        v-if="currentUser.firm_access !== 'basic'"
                      >
                        <v-list-tile-title> Remove Client </v-list-tile-title>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </v-layout>
              </td>
            </tr>
          </template>
        </v-data-table>

        <v-layout
          v-else-if="clientView === 'users' && $vuetify.breakpoint.xsOnly"
        >
          <template v-if="pageLoading"> Loading </template>
          <v-flex v-else>
            <ClientCard
              v-for="client in usersMapped"
              :change-current-company="changeCurrentCompany"
              :is-individual="isIndividual"
              :has-new-message="hasNewMessageForUser"
              :client="client"
              :key="`client_${client.id}`"
            >
              <template v-slot:actions>
                <v-list>
                  <v-list-tile
                    @click="
                      individualEntity(props.item) &&
                      !showEntityInformation(props.item)
                        ? openEditClientInfo(client)
                        : openEditEntityDialog(individualEntity(client))
                    "
                  >
                    <v-list-tile-title>
                      Edit Client Information
                    </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile
                    @click="openEditEntityDialog(client.companies[0])"
                    v-if="
                      client.companies.length === 1 &&
                        !isIndividual(client.companies[0])
                    "
                  >
                    <v-list-tile-title> Edit Company Info </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile
                    @click="openSelectEntityDialogToEdit(client)"
                    v-if="client.companies.length > 1"
                  >
                    <v-list-tile-title> Edit Entity Info </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile
                    @click="openAddEntityDialog(client)"
                    v-if="currentUser.firm_access !== 'basic'"
                  >
                    <v-list-tile-title> Add Entity </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile
                    @click="openSelectEntityDialogToDelete(client)"
                    v-if="
                      client.companies.length > 1 &&
                        currentUser.firm_access !== 'basic'
                    "
                  >
                    <v-list-tile-title> Remove Entity </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile
                    @click="openRemoveClientConfirmationDialog(client)"
                    v-if="currentUser.firm_access !== 'basic'"
                  >
                    <v-list-tile-title> Remove Client </v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </template>
            </ClientCard>
          </v-flex>
        </v-layout>

        <!-- Entity View Table -->
        <v-data-table
          v-if="clientView === 'entities'"
          :headers="entityViewHeaders"
          :items="entitiesMapped"
          :search="clientSearch"
          :loading="tableLoading"
          :pagination.sync="entityPagination"
          :rows-per-page-items="entityPagination.options"
          :custom-filter="customEntityFilter"
          class="clients-table text-xs-left"
          select-all
          v-model="selectedEntities"
        >
          <template slot="headerCell" slot-scope="props">
            <template v-if="props.header.text === 'Assigned to'">
              <v-tooltip bottom>
                <template slot="activator">
                  <v-layout align-center>
                    {{ props.header.text }}
                    <v-icon size="15" class="ml-1"> help_outline </v-icon>
                  </v-layout>
                </template>
                <span>
                  Assigned user in the firm is the contact point for the client.
                </span>
              </v-tooltip>
            </template>
            <template v-else>
              <span>
                {{ props.header.text }}
              </span>
            </template>
          </template>
          <template slot="items" slot-scope="props">
            <tr :key="props.id" slot="header">
              <td>
                <v-checkbox
                  v-model="props.selected"
                  primary
                  color="lightgrey"
                  hide-details
                ></v-checkbox>
              </td>

              <!-- Column: Name -->
              <td class="black--text ph-no-capture">
                <template v-if="pageLoading"> Loading </template>
                <template v-else>
                  <v-layout align-center wrap>
                    <v-tooltip bottom>
                      <v-icon
                        slot="activator"
                        class="mr-2"
                        :class="
                          props.item.notification_subscribed ||
                          props.item.assigned_advisor_id === currentUser.id
                            ? 'primary--text'
                            : 'darkgrey--text'
                        "
                        :style="
                          props.item.assigned_advisor_id === currentUser.id
                            ? 'opacity: 60%;'
                            : ''
                        "
                        @click="
                          openNotificationSubscriptionDialog(props.item.id)
                        "
                      >
                        {{
                          props.item.notification_subscribed ||
                          props.item.assigned_advisor_id === currentUser.id
                            ? 'notifications_active'
                            : 'notifications_none'
                        }}
                      </v-icon>
                      <span
                        v-if="
                          props.item.notification_subscribed ||
                            props.item.assigned_advisor_id === currentUser.id
                        "
                        >Notifications On</span
                      >
                      <span v-else>Notifications Off</span>
                    </v-tooltip>
                    <a
                      class="client-name mr-1"
                      @click="
                        changeCurrentCompany(
                          $event,
                          props.item.id,
                          `/admin-dashboard/${props.item.hash}`,
                        )
                      "
                      :href="'/admin-dashboard/' + props.item.hash"
                      >{{ props.item.tax_return_name }}</a
                    >
                    <span
                      v-if="
                        shouldShowExternalUserId
                          ? props.item.external_user_id
                          : props.item.external_entity_id
                      "
                      @click="
                        changeCurrentCompany(
                          $event,
                          props.item.id,
                          `/admin-dashboard/${props.item.hash}`,
                        )
                      "
                      :href="'/admin-dashboard/' + props.item.hash"
                      style="cursor: pointer"
                    >
                      <i
                        >({{
                          shouldShowExternalUserId
                            ? props.item.external_user_id
                            : props.item.external_entity_id
                        }})
                      </i>
                    </span>
                    <v-chip
                      v-if="props.item.inactive"
                      color="error"
                      small
                      class="white--text"
                      >Inactive</v-chip
                    >
                    <v-chip
                      v-else-if="props.item.readonly_access"
                      color="warning"
                      small
                      class="black--text"
                      >Readonly</v-chip
                    >
                    <v-tooltip
                      bottom
                      v-if="hasNewMessageInRelevantConversation(props.item)"
                    >
                      <v-icon
                        slot="activator"
                        color="success"
                        @click="
                          $emit(
                            'openConversationDialog',
                            props.item.relevant_conversation_id,
                          )
                        "
                        >mail</v-icon
                      >
                      <span>New Message</span>
                    </v-tooltip>
                  </v-layout>
                </template>
              </td>

              <!-- Column: Entity Type -->
              <td class="text-xs-left">
                <template v-if="pageLoading"> Loading </template>
                <template v-else>
                  {{ props.item.entity_type }}
                </template>
              </td>

              <!-- Column: Firm Group Name -->
              <td class="text-xs-left ph-no-capture">
                <template v-if="pageLoading"> Loading </template>
                <template v-else>
                  {{ props.item.client_name }}
                  <v-chip
                    v-if="!props.item.user.paying"
                    color="error"
                    small
                    class="white--text"
                    >Inactive</v-chip
                  >
                  <v-chip
                    v-else-if="props.item.user.readonly_access"
                    color="warning"
                    small
                    class="black--text"
                    >Readonly</v-chip
                  >
                  <v-chip small round v-if="props.item.user.for_testing"
                    >Test</v-chip
                  >
                  <template
                    v-if="showGroupFilters && props.item.user.firm_group"
                  >
                    <v-chip small>
                      {{ props.item.user.firm_group.name }}
                    </v-chip>
                  </template>
                </template>
              </td>

              <!-- Column: Client tags -->
              <td
                class="text-xs-left"
                v-if="currentUser.overview_setting.use_contact_tags"
              >
                <v-layout v-if="props.item.contact_tags < 2">
                  <v-chip
                    :key="i"
                    v-for="(tag, i) in props.item.contact_tags"
                    small
                  >
                    {{ tag.name }}
                  </v-chip>
                </v-layout>
                <v-layout v-else>
                  <v-chip
                    :key="i"
                    v-for="(tag, i) in props.item.contact_tags"
                    small
                    v-if="i < 2"
                  >
                    {{ tag.name }}
                  </v-chip>
                  <v-tooltip bottom v-if="props.item.contact_tags.length > 2">
                    <v-chip slot="activator" small>
                      <span class="caption"
                        >+{{ props.item.contact_tags.length - 2 }}</span
                      >
                    </v-chip>
                    <span
                      ><div
                        v-for="(tag, i) in props.item.contact_tags"
                        v-if="i >= 2"
                      >
                        {{ tag.name }}
                      </div></span
                    >
                  </v-tooltip>
                </v-layout>
              </td>

              <!-- Column: Entity tags -->
              <td
                class="text-xs-left"
                v-if="currentUser.overview_setting.use_entity_tags"
              >
                <v-layout v-if="props.item.entity_tags < 2">
                  <v-chip
                    :key="i"
                    v-for="(tag, i) in props.item.entity_tags"
                    small
                  >
                    {{ tag.name }}
                  </v-chip>
                </v-layout>
                <v-layout v-else>
                  <v-chip
                    :key="i"
                    v-for="(tag, i) in props.item.entity_tags"
                    small
                    v-if="i < 2"
                  >
                    {{ tag.name }}
                  </v-chip>
                  <v-tooltip bottom v-if="props.item.entity_tags.length > 2">
                    <v-chip slot="activator" small>
                      <span class="caption"
                        >+{{ props.item.entity_tags.length - 2 }}</span
                      >
                    </v-chip>
                    <span
                      ><div
                        v-for="(tag, i) in props.item.entity_tags"
                        v-if="i >= 2"
                      >
                        {{ tag.name }}
                      </div></span
                    >
                  </v-tooltip>
                </v-layout>
              </td>

              <!-- Column: Assigned to -->
              <td class="text-xs-left ph-no-capture">
                <v-autocomplete
                  v-if="
                    currentUser.firm_access !== 'basic' || allowAssignInGroup
                  "
                  class="default-input body-1"
                  hide-details
                  solo
                  :key="`assigned_${props.item.id}`"
                  :items="
                    filterFirmUsers(
                      props.item.assigned_advisor_id,
                      props.item.firm_group,
                    )
                  "
                  :value="props.item.assigned_advisor_id"
                  placeholder="Not Assigned"
                  item-text="name"
                  item-value="id"
                  @change="
                    newAssignedAdvisorId =>
                      updateAssignment(props.item, newAssignedAdvisorId)
                  "
                />
                <div
                  v-else-if="props.item.assigned_advisor_id && firmUsers.length"
                >
                  {{
                    firmUsers.find(a => a.id === props.item.assigned_advisor_id)
                      .name
                  }}
                </div>
                <div v-else class="grey--text text--darken-1">-</div>
              </td>

              <!-- Column: Actions -->
              <td>
                <v-layout align-center>
                  <v-menu
                    transition="slide-y-transition"
                    bottom
                    left
                    nudge-bottom="30"
                    class="my-1"
                  >
                    <v-icon slot="activator" color="#0a1a25">
                      more_vert
                    </v-icon>
                    <v-list>
                      <v-list-tile @click="openEditEntityDialog(props.item)">
                        <v-list-tile-title>
                          Edit Entity Info
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile @click="openEditClientInfo(props.item.user)">
                        <v-list-tile-title>
                          Edit Client Information
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        @click="
                          openNotificationSubscriptionDialog(props.item.id)
                        "
                      >
                        <v-list-tile-title>
                          Edit Notification Setting
                        </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        @click="openRemoveEntityConfirmationDialog(props.item)"
                        v-if="
                          currentUser.firm_access !== 'basic' &&
                            props.item.other_companies.length > 1
                        "
                      >
                        <v-list-tile-title> Remove Entity </v-list-tile-title>
                      </v-list-tile>
                      <v-list-tile
                        @click="openRemoveClientConfirmationDialog(props.item)"
                        v-if="
                          currentUser.firm_access !== 'basic' &&
                            props.item.other_companies.length === 1
                        "
                      >
                        <v-list-tile-title>
                          Remove Entity and Client
                        </v-list-tile-title>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </v-layout>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Signature Requests -->
      <v-card-text v-else-if="active === 2">
        <v-layout align-center wrap class="mb-2">
          <AllSignatureRequests
            :all-signature-requests="allSignatureRequests"
            :table-loading="signatureRequestTableLoading"
            @refresh-table="refreshTable"
            @refresh-table-until-sent="refreshSignatureRequestsUntilSent"
          />
        </v-layout>
      </v-card-text>

      <!-- Tax Return Deliveries -->
      <v-card-text v-else-if="active === 3">
        <v-layout align-center wrap class="mb-2">
          <AllTaxReturnDeliveries
            :tax-returns="allTaxReturns"
            :table-loading="taxReturnDeliveryTableLoading"
            @refresh-table="getAllTaxReturns"
            @refresh-table-until-sent="refreshTaxReturnsUntilSent"
          />
        </v-layout>
      </v-card-text>

      <!-- Client Activities -->
      <v-card-text v-else-if="active === 4">
        <v-layout align-center wrap class="mb-2">
          <ClientActivities
            :all-activities="allActivities"
            :table-loading="tableLoading"
            @refresh-table="refreshTable"
            @handleNotification="handleNotification"
          />
        </v-layout>
      </v-card-text>

      <!-- Todos -->
      <v-card-text v-else-if="active === 5">
        <v-layout align-center wrap class="mb-2">
          <TodoTasks
            :table-loading="tableLoading"
            :todo-tasks="allTodoTasks"
            @refresh-table="refreshTable"
            @on-selection-change="selectedTaskIds = $event"
          />
        </v-layout>
      </v-card-text>

      <!-- Review & Export -->
      <v-card-text v-else-if="active === 6">
        <v-layout>
          <v-flex xs6 class="px-2">
            <div class="text-xs-left">Search</div>
            <v-text-field
              v-model="reviewAndExportSearch"
              class="default-input subheading"
              solo
              hide-details
              placeholder="Look for Entity"
            />
          </v-flex>
          <v-flex>
            <div class="text-xs-left">Year</div>
            <v-select
              v-model="reviewAndExportYear"
              :items="yearOptions"
              hide-details
              class="default-input body-1"
              solo
              small
              style="max-width: 120px"
            />
          </v-flex>
          <!-- <v-btn
            @click="startExport"
            color="primary"
            class="white--text text-none"
            >Export All Scheduled</v-btn
          > -->
        </v-layout>
        <v-layout align-center wrap class="mb-2">
          <ReviewAndExportTable
            :table-loading="tableLoading"
            :entities-with-tax-forms="entitiesWithTaxForms"
            @refresh="refreshTable"
            :selected-year="reviewAndExportYear"
            :pagination.sync="reviewAndExportPagination"
            :total-items="reviewAndExportTotalItems"
          />
        </v-layout>
      </v-card-text>

      <!-- Questionnaire table -->
      <v-card-text v-else>
        <v-layout align-center wrap class="mb-2">
          <template v-if="$vuetify.breakpoint.smAndUp">
            <!-- Search -->
            <v-flex xs12 sm4 class="pr-2">
              <div class="text-xs-left">Search</div>
              <v-text-field
                v-model="questionnaireSearch"
                class="default-input subheading"
                solo
                hide-details
                placeholder="Look for entity or questionnaire name"
                ref="questionnaireSearchInput"
                @keyup.native.enter="$refs.questionnaireSearchInput.blur()"
                @blur="refreshQuestionnairesWithDebounce"
                @input="refreshQuestionnairesWithDebounce"
              />
            </v-flex>

            <!-- Filter: Year -->
            <v-flex xs2 class="pr-2">
              <div class="text-xs-left">Year</div>
              <v-select
                v-model="selectedYear"
                :items="yearOptionsFromPastQuestionnaires"
                hide-details
                solo
                class="default-input"
                @change="updateFilterRecord('selectedYear', selectedYear)"
                :class="getHighlightedClass('selectedYear', selectedYear)"
              />
            </v-flex>

            <!-- Filter: Entity -->
            <v-flex xs2 class="px-2">
              <div class="text-xs-left">Entity</div>
              <v-select
                v-model="entityFilter"
                :items="entitiesWithAll"
                hide-details
                solo
                class="default-input"
                @change="updateFilterRecord('entityFilter', entityFilter)"
                :class="getHighlightedClass('entityFilter', entityFilter)"
              />
            </v-flex>

            <!-- Filter: Type -->
            <v-flex xs2 class="px-2">
              <div class="text-xs-left">Type</div>
              <v-select
                v-model="questionnaireType"
                :items="questionnaireTypes"
                hide-details
                solo
                class="default-input"
                @change="
                  updateFilterRecord('questionnaireType', questionnaireType)
                "
                :class="
                  getHighlightedClass('questionnaireType', questionnaireType)
                "
              />
            </v-flex>

            <!-- Filter: Status -->
            <v-flex xs2 class="px-2">
              <sc-flex :is-y-centered="true" :gap="8">
                <sc-flex :is-column="true">
                  <div class="text-xs-left">Status</div>
                  <v-select
                    v-model="questionnaireStatus"
                    @change="
                      updateFilterRecord(
                        'questionnaireStatus',
                        questionnaireStatus,
                      )
                    "
                    :class="
                      getHighlightedClass(
                        'questionnaireStatus',
                        questionnaireStatus,
                      )
                    "
                    :items="questionnaireStatusList"
                    hide-details
                    solo
                    class="default-input overview_dropdown_input"
                    label="Status"
                    item-text="name"
                    item-value="value"
                  >
                    <template v-slot:selection="{ item, index }">
                      <span v-if="index === 0">{{ item.name }} </span>
                      <span v-if="index === 1" class="grey--text caption"
                        >&nbsp;(+{{
                          questionnaireStatus.length - 1
                        }}
                        others)</span
                      >
                    </template>
                    <template v-slot:append-item>
                      <v-checkbox
                        v-model="questionnaireStatusIncludeLocked"
                        @change="
                          updateFilterRecord(
                            'questionnaireStatusIncludeLocked',
                            questionnaireStatusIncludeLocked,
                          )
                        "
                        :class="
                          getHighlightedClass(
                            'questionnaireStatusIncludeLocked',
                            questionnaireStatusIncludeLocked,
                          )
                        "
                        primary
                        label="Include Locked"
                        color="primary"
                        hide-details
                        class="include-locked-checkbox"
                      ></v-checkbox>
                    </template>
                  </v-select>
                </sc-flex>
              </sc-flex>
            </v-flex>
          </template>
        </v-layout>
        <v-layout align-center wrap class="mb-2" id="questionnaire-filters">
          <!-- Filter: Assigned to -->
          <v-flex xs2 class="pr-2" v-if="currentUser.firm_access !== 'basic'">
            <div class="text-xs-left">Assigned To</div>
            <v-select
              v-model="selectedAssignedUser"
              :items="getFirmUsersFilterOptions()"
              item-text="name"
              @change="
                updateFilterRecord('selectedAssignedUser', selectedAssignedUser)
              "
              :class="
                getHighlightedClass(
                  'selectedAssignedUser',
                  selectedAssignedUser,
                )
              "
              item-value="id"
              hide-details
              solo
              :placeholder="
                projectViewEnabled
                  ? 'Search for entity or project name'
                  : 'Search for entity or questionnaire name'
              "
              class="default-input"
            />
          </v-flex>

          <!-- Filter: Preparer -->
          <v-flex
            xs2
            class="pr-2"
            v-if="currentUser.firm_access !== 'basic' && useFirmRoles"
          >
            <div class="text-xs-left">Preparer</div>
            <v-select
              v-model="selectedPreparerUser"
              :items="getFirmUsersFilterOptions()"
              @change="
                updateFilterRecord('selectedPreparerUser', selectedPreparerUser)
              "
              :class="
                getHighlightedClass(
                  'selectedPreparerUser',
                  selectedPreparerUser,
                )
              "
              item-text="name"
              item-value="id"
              hide-details
              solo
              placeholder="Select Preparer"
              class="default-input"
            />
          </v-flex>

          <!-- Filter: Client Manager -->
          <v-flex
            xs2
            class="pr-2"
            v-if="currentUser.firm_access !== 'basic' && useFirmRoles"
          >
            <div class="text-xs-left">Client Manager</div>
            <v-select
              v-model="selectedClientManagerUser"
              @change="
                updateFilterRecord(
                  'selectedClientManagerUser',
                  selectedClientManagerUser,
                )
              "
              :class="
                getHighlightedClass(
                  'selectedClientManagerUser',
                  selectedClientManagerUser,
                )
              "
              :items="getFirmUsersFilterOptions()"
              item-text="name"
              item-value="id"
              hide-details
              solo
              placeholder="Select Client Manager"
              class="default-input"
            />
          </v-flex>

          <!-- Sent by -->
          <v-flex
            xs2
            class="px-2"
            v-if="currentUser.overview_setting.filter_by_sender"
          >
            <div class="text-xs-left">Sent By</div>
            <v-select
              v-model="selectedSender"
              @change="updateFilterRecord('selectedSender', selectedSender)"
              :class="getHighlightedClass('selectedSender', selectedSender)"
              :items="getFirmUsersFilterOptions()"
              item-text="name"
              item-value="id"
              hide-details
              solo
              placeholder="Select Sender"
              class="default-input"
            />
          </v-flex>

          <!-- Filter: Client Tags -->
          <v-flex
            xs2
            class="px-2"
            v-if="currentUser.overview_setting.use_contact_tags"
          >
            <div class="text-xs-left">
              Client Tags
              <v-tooltip bottom>
                <v-icon
                  slot="activator"
                  class="darkgrey--text ml-1"
                  size="15"
                  style="cursor: pointer"
                  @click="
                    manageTagsDialogOpened = true;
                    manageTagType = 'client-tags';
                  "
                >
                  edit
                </v-icon>
                <span>Edit Tags for Firm</span>
              </v-tooltip>
            </div>
            <v-select
              chips
              small-chips
              multiple
              @change="
                updateFilterRecord(
                  'selectedClientTagsFilter',
                  selectedClientTagsFilter,
                )
              "
              :class="
                getHighlightedClass(
                  'selectedClientTagsFilter',
                  selectedClientTagsFilter,
                )
              "
              v-model="selectedClientTagsFilter"
              :items="firmContactTagsWithNone"
              hide-details
              placeholder="Select Tags to Filter"
              solo
              class="default-input"
            />
          </v-flex>

          <!-- Filter: Entity Tags -->
          <v-flex
            xs2
            class="px-2"
            v-if="currentUser.overview_setting.use_entity_tags"
          >
            <div class="text-xs-left">
              Entity Tags
              <v-tooltip bottom>
                <v-icon
                  slot="activator"
                  class="darkgrey--text ml-1"
                  size="15"
                  style="cursor: pointer"
                  @click="
                    manageTagsDialogOpened = true;
                    manageTagType = 'entity-tags';
                  "
                >
                  edit
                </v-icon>
                <span>Edit Tags for Firm</span>
              </v-tooltip>
            </div>
            <v-select
              multiple
              v-model="selectedEntityTagsFilter"
              :items="firmEntityTagsWithNone"
              @change="
                updateFilterRecord(
                  'selectedEntityTagsFilter',
                  selectedEntityTagsFilter,
                )
              "
              :class="
                getHighlightedClass(
                  'selectedEntityTagsFilter',
                  selectedEntityTagsFilter,
                )
              "
              hide-details
              placeholder="Select Tags to Filter"
              solo
              class="default-input"
            />
          </v-flex>

          <!-- Filter: Firm Group -->
          <v-flex xs2 v-if="showGroupFilters" class="px-2">
            <div class="text-xs-left">Firm group</div>
            <v-select
              v-model="selectedFirmGroup"
              @change="
                updateFilterRecord('selectedFirmGroup', selectedFirmGroup)
              "
              :items="[
                {
                  name: 'All',
                  id: -1,
                },
                ...firmGroups,
              ]"
              item-text="name"
              item-value="id"
              hide-details
              solo
              class="default-input"
            />
          </v-flex>

          <!-- Filter: Custom status -->
          <v-flex xs3 class="px-2" v-if="customStatusesEnabled">
            <div class="text-xs-left">Firm-internal Status</div>
            <v-select
              chips
              small-chips
              multiple
              @change="
                updateFilterRecord(
                  'selectedFirmCustomStatusFilter',
                  selectedFirmCustomStatusFilter,
                )
              "
              :class="
                getHighlightedClass(
                  'selectedFirmCustomStatusFilter',
                  selectedFirmCustomStatusFilter,
                )
              "
              v-model="selectedFirmCustomStatusFilter"
              :items="firmCustomStatusesWithNone"
              item-text="firm_user_display_status"
              item-value="id"
              hide-details
              placeholder="Select Firm-internal Status to Filter"
              solo
              class="default-input"
            />
          </v-flex>

          <!-- Filter: Hide Inactive -->
          <v-flex xs2 class="px-2">
            <v-switch
              v-model="hideInactive"
              hide-details
              @change="updateFilterRecord('hideInactive', hideInactive)"
              label="Hide Inactive"
              solo
              class="default-input my-0 py-0 mr-2"
            />
          </v-flex>

          <v-flex xs2 class="px-2">
            <!-- Generate Report -->
            <sc-button-group v-if="active === 0">
              <!-- Action: Clear Filters -->

              <sc-button
                v-if="hasStoredFilters"
                type="secondary"
                class="mt-2"
                size="small"
                @click="clearFilters()"
                icon-left="close"
                >Clear Filters</sc-button
              >

              <sc-button
                v-if="currentUser.firm_access !== 'partner'"
                type="secondary"
                size="small"
                @click="generateQuestionnaireReport()"
                >Generate Report for {{ selectedYear }}</sc-button
              >
            </sc-button-group>
          </v-flex>
        </v-layout>

        <!-- Partial: Table Info -->
        <aside
          v-if="selectedQuestionnaireCount > 0"
          class="table-helper"
          :is-between="true"
          :gap="8"
        >
          <sc-button-group>
            <sc-button
              @click="selectAllQuestionnaires()"
              v-if="someQuestionnairesNotSelected"
              type="ghost"
              size="small"
              icon-left="add"
              >Select All ({{ totalQuestionnaires }})</sc-button
            >
            <sc-label type="info"
              >{{ selectedQuestionnaireCount }} selected</sc-label
            >
          </sc-button-group>
          <sc-button-group>
            <sc-button
              @click="openSelectedQuestionnairesForExport()"
              type="primary"
              size="small"
              icon-right="download"
              >Export Questionnaires</sc-button
            >
          </sc-button-group>
        </aside>

        <!-- Questionnaire Table -->
        <v-data-table
          :headers="questionnaireHeaders"
          :items="questionnairesMapped"
          :loading="tableLoading"
          :pagination.sync="questionnairePagination"
          :rows-per-page-items="questionnairePagination.options"
          :select-all="$vuetify.breakpoint.mdAndUp"
          :total-items="totalQuestionnaires"
          class="questionnaires-table text-xs-left"
          disable-initial-sort
          v-model="selectedQuestionnaires"
        >
          <template slot="items" slot-scope="props">
            <tr :key="props.id">
              <!-- Column: Check -->
              <td class="hidden-sm-and-down" data-td="check">
                <v-checkbox
                  v-model="props.selected"
                  primary
                  color="lightgrey"
                  hide-details
                ></v-checkbox>
              </td>

              <OverviewQuestionnairesTableClientNameDataCell
                :change-current-company="changeCurrentCompany"
                :client="props.item.client"
                :company-id="props.item.company_id"
                :hash="props.item.hash"
                :is-completed="props.item.completed"
                :is-locked="props.item.locked"
                :page-loading="pageLoading"
                :updated-at-by-client="props.item.updated_at_by_client"
              />

              <!-- Column: Entity -->
              <td data-td="entity_id" v-if="shouldShowExternalUserId">
                <a
                  @click="
                    changeCurrentCompany(
                      $event,
                      props.item.company_id,
                      `/admin-dashboard/${props.item.company.hash_id}`,
                    )
                  "
                  :href="'/admin-dashboard/' + props.item.company.hash_id"
                  class="overview-table__external-id"
                >
                  <sx-tiny color="currentcolor">
                    {{
                      props.item.company &&
                      props.item.company.user &&
                      props.item.company.user.external_user_id
                        ? props.item.company.user.external_user_id
                        : ''
                    }}
                  </sx-tiny>
                </a>
              </td>

              <!-- Column: Year -->
              <td data-td="year">
                <sx-tiny>{{ props.item.tax_year }}</sx-tiny>
              </td>

              <!-- Column: Name -->
              <td data-td="name">
                <v-layout align-center>
                  <div>
                    <a
                      class="black--text"
                      style="cursor: pointer"
                      @click="openQuestionnaireDialog(props.item)"
                      target="_self"
                    >
                      <sx-tiny>
                        <v-tooltip bottom>
                          <span slot="activator">{{
                            props.item.is_open_item
                              ? props.item.formatted_name
                              : props.item.name
                          }}</span>
                          <span v-if="props.item.is_draft"> Edit draft </span>
                          <span v-else>
                            Sent on
                            {{ moment(props.item.sent_at).format('ll') }}
                          </span>
                        </v-tooltip>
                        <v-tooltip
                          bottom
                          v-if="props.item.track_changes"
                          class="ml-1"
                        >
                          <v-icon
                            slot="activator"
                            size="18"
                            class="darkgrey--text"
                          >
                            circle_notifications
                          </v-icon>
                          <span> Tracking changes </span>
                        </v-tooltip>
                      </sx-tiny>
                    </a>
                    <a
                      v-if="props.item.is_draft"
                      class="font-weight-bold d-block"
                      @click="openQuestionnaireDialog(props.item)"
                    >
                      <span>(Edit draft)</span>
                    </a>
                  </div>
                </v-layout>
              </td>

              <!-- Column: Status -->
              <td class="text-xs-left" data-td="status">
                <template v-if="pageLoading || props.item.processing">
                  <v-tooltip bottom v-if="props.item.processing">
                    <v-layout
                      align-center
                      justify-start
                      slot="activator"
                      style="cursor: default"
                    >
                      <v-progress-circular
                        indeterminate
                        size="25"
                        color="primary"
                      ></v-progress-circular>
                    </v-layout>
                    <span> Loading </span>
                  </v-tooltip>
                </template>
                <template v-else>
                  <v-layout slot="activator" class="text-xs-left">
                    <v-tooltip bottom>
                      <div slot="activator">
                        <sx-percentage-indicator
                          :total="props.item.sectionCount"
                          :count="props.item.sectionDoneCount"
                          :is-locked="props.item.locked"
                          :is-in-progress="Boolean(props.item.in_progress)"
                          :is-opened="!!props.item.opened_timestamp"
                          :is-completed="props.item.completed"
                        >
                          <span v-if="props.item.completed">Completed</span>
                          <span v-else-if="props.item.is_draft">Draft</span>
                          <span v-else-if="props.item.status > 0">{{
                            props.item.currentStep
                          }}</span>
                          <span v-else-if="props.item.in_progress"
                            >In Progress</span
                          >
                          <span v-else-if="props.item.opened_timestamp"
                            >Opened</span
                          >
                          <span v-else>Sent</span>
                        </sx-percentage-indicator>
                      </div>
                      <v-layout column class="body-1 text-xs-left">
                        <v-flex v-if="props.item.locked">
                          Open to see details
                        </v-flex>
                        <v-flex v-else-if="props.item.batch_sending">
                          To be sent out soon
                        </v-flex>
                        <template v-else>
                          <v-flex>
                            {{
                              props.item.opened_timestamp
                                ? `Started ${moment(
                                    props.item.opened_timestamp,
                                  ).format('lll')}`
                                : 'Not opened yet'
                            }}
                          </v-flex>
                          <v-flex
                            v-if="props.item.earliest_remind_date"
                            color="white"
                          >
                            Client estimated completion date:
                            {{
                              moment(
                                moment(
                                  props.item.earliest_remind_date,
                                ).toISOString(),
                                moment.ISO_8601,
                              ).format('ll')
                            }}.
                          </v-flex>
                          <br />
                          <v-flex
                            v-if="
                              props.item.sections.every(
                                a => !a.marked_completed,
                              )
                            "
                          >
                            No
                            {{ projectViewEnabled ? 'step' : 'section' }}
                            completed
                          </v-flex>
                          <v-flex
                            v-else-if="
                              props.item.sections.every(a => a.marked_completed)
                            "
                          >
                            All sections completed
                          </v-flex>
                          <v-flex v-else>
                            Incomplete Sections:
                            <v-flex
                              v-for="incompleteSection in firstThreeIncompleteSections(
                                props.item,
                              )"
                              :key="incompleteSection.id"
                            >
                              • {{ incompleteSection.name }}
                            </v-flex>
                            <v-flex
                              v-if="
                                props.item.sections.filter(
                                  a => !a.marked_completed,
                                ).length > 3
                              "
                            >
                              ...
                            </v-flex>
                          </v-flex>
                        </template>
                      </v-layout>
                    </v-tooltip>
                  </v-layout>
                </template>
              </td>

              <!-- Column: Custom status -->
              <td class="text-xs-left" v-if="customStatusesEnabled">
                <template v-if="pageLoading"> Loading </template>
                <v-layout align-center v-else>
                  <v-menu
                    transition="slide-y-transition"
                    bottom
                    nudge-bottom="30"
                    class="my-1"
                  >
                    <v-chip slot="activator" small>
                      {{
                        props.item.firm_custom_status
                          ? props.item.firm_custom_status
                              .firm_user_display_status
                          : 'No Status'
                      }}
                    </v-chip>
                    <v-list>
                      <v-list-tile
                        v-for="(status, i) in firmCustomStatusesWithNone"
                        :key="i"
                        :disabled="
                          status.id === props.item.firm_custom_status_id
                        "
                        @click="
                          openSendCustomStatusChangeDialog(props.item, status)
                        "
                      >
                        <v-list-tile-title>
                          {{ status.firm_user_display_status }}
                        </v-list-tile-title>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </v-layout>
              </td>

              <!-- Column: Notes -->
              <td class="text-xs-left" data-td="notes">
                <v-tooltip bottom>
                  <v-layout align-center slot="activator">
                    <sx-note-count :note-count="props.item.notes.length" />
                  </v-layout>
                  <div>
                    <div v-if="props.item.notes.length" class="text-xs-left">
                      <div
                        v-for="(note, noteKey) in props.item.notes"
                        :key="noteKey"
                      >
                        {{ noteKey + 1 }}. {{ note.text }}
                      </div>
                    </div>
                    <div v-else>0 Notes</div>
                  </div>
                </v-tooltip>
              </td>

              <!-- Column: Document Count -->
              <td class="text-xs-left" data-td="documents">
                <template v-if="pageLoading">-</template>
                <template v-else>
                  <v-tooltip bottom v-if="!props.item.locked">
                    <v-layout align-center slot="activator">
                      <sx-count-box
                        :count="props.item.documentCount"
                        :total="props.item.documentTotal"
                      />
                    </v-layout>
                    <v-flex class="text-xs-left">
                      <span
                        v-if="
                          props.item.documentCount === 0 &&
                            props.item.documentTotal === 0
                        "
                        >No documents requested.</span
                      >
                      <span v-else-if="props.item.documents_needed.length === 0"
                        >All Documents Provided.</span
                      >
                      <div v-else>
                        <b
                          >{{
                            getDocumentNeededCount(props.item.documents_needed)
                          }}
                          document{{
                            getDocumentNeededCount(
                              props.item.documents_needed,
                            ) > 1
                              ? 's'
                              : ''
                          }}
                          still needed:</b
                        >
                        <div v-for="document in props.item.documents_needed">
                          • {{ getRequiredDocumentName(document) }}
                        </div>
                      </div>
                    </v-flex>
                  </v-tooltip>
                  <v-flex v-else>
                    <sx-count-box
                      :is-disabled="true"
                      :count="props.item.documentCount"
                      :total="props.item.documentTotal"
                    />
                  </v-flex>
                </template>
              </td>

              <!-- Column: Last activity including client logon -->
              <td class="text-xs-center" data-td="activity">
                <template v-if="pageLoading"> Loading </template>
                <template v-else>
                  <v-tooltip bottom>
                    <v-layout
                      align-center
                      slot="activator"
                      class="text-xs-left"
                    >
                      <v-flex
                        class="darkgrey--text no-wrap"
                        v-if="props.item.last_activity"
                      >
                        {{ moment(props.item.last_activity).fromNow() }}
                      </v-flex>
                      <v-flex class="black--text" v-else> - </v-flex>
                    </v-layout>
                    <sx-flex :is-column="true" :gap="4">
                      <sx-tiny v-if="props.item.last_activity_at" color="white">
                        Last Client Logon:
                        {{ moment(props.item.last_activity_at).fromNow() }}
                      </sx-tiny>
                      <hr v-if="props.item.last_activity_at" />
                      <v-flex v-if="props.item.last_followed_up">
                        Last reminder was sent on
                        {{
                          moment(
                            moment(props.item.last_followed_up).toISOString(),
                            moment.ISO_8601,
                          ).format('ll')
                        }}.
                      </v-flex>
                      <v-flex v-else>
                        No reminders since the sent date of
                        {{
                          moment(
                            moment(props.item.sent_at).toISOString(),
                            moment.ISO_8601,
                          ).format('ll')
                        }}.
                      </v-flex>
                      <v-flex v-if="currentUserFirm && !props.item.completed">
                        {{
                          getNextReminderDate(props.item, null, currentUserFirm)
                        }}
                      </v-flex>
                    </sx-flex>
                  </v-tooltip>
                </template>
              </td>

              <!-- Column: Assigned to -->
              <td class="text-xs-left ph-no-capture" data-td="assigned">
                <template v-if="pageLoading"> Loading </template>
                <template v-else>
                  <sx-tiny>{{
                    getFirmUserName(
                      get(props, 'item.company.user.assigned_advisor_id', null),
                    )
                  }}</sx-tiny>
                </template>
              </td>

              <!-- Column: Due date -->
              <td data-td="due">
                <template v-if="pageLoading"> Loading </template>
                <template v-else>
                  <!-- Due date N/A -->
                  <v-layout v-if="!props.item.due_date">
                    <sx-tiny>&ndash;</sx-tiny>
                  </v-layout>
                  <sx-flex
                    v-else
                    :is-column="true"
                    :class="
                      props.item.completed || props.item.locked
                        ? 'overview__table__duedate--completed'
                        : ''
                    "
                    class="overview__table__duedate"
                  >
                    <!-- Date -->
                    <sx-tiny color="currentcolor">{{
                      moment(props.item.due_date).format('ll')
                    }}</sx-tiny>
                    <!-- Urgency -->
                    <!-- Past due -->
                    <div
                      v-if="pastDue(props.item.due_date)"
                      class="overview__table__duedate--error"
                    >
                      <sx-icon
                        name="error-circle"
                        :size="12"
                        color="currentcolor"
                      />
                      <sx-tiny color="currentcolor"><b>Past Due</b></sx-tiny>
                    </div>
                    <!-- 1 week left -->
                    <div
                      v-else-if="oneWeekAway(props.item.due_date)"
                      class="overview__table__duedate--warning"
                    >
                      <sx-icon name="warning" :size="12" color="currentcolor" />
                      <sx-tiny color="currentcolor"><b>Next Week</b></sx-tiny>
                    </div>
                  </sx-flex>
                </template>
              </td>

              <!-- Column: Next Reminder Date -->
              <td
                data-td="next_reminder"
                v-if="currentUser.overview_setting.show_next_reminder_date"
              >
                <template v-if="pageLoading"> Loading </template>
                <template v-else>
                  <v-flex
                    v-if="getNextReminderDateString(props.item, currentFirm)"
                    class="overview-table__earliest-remind-date"
                  >
                    <sx-flex :is-y-centered="true" :gap="4">
                      <sx-icon
                        :size="14"
                        name="time-lapse"
                        color="currentcolor"
                      />
                      <sx-tiny color="currentcolor">
                        {{ getNextReminderDateString(props.item, currentFirm) }}
                      </sx-tiny>
                    </sx-flex>
                  </v-flex>
                  <v-flex
                    v-else
                    class="overview-table__earliest-remind-date overview-table__earliest-remind-date--empty"
                  >
                    <v-tooltip bottom>
                      <div slot="activator">
                        <sx-icon :size="16" name="na" color="currentcolor" />
                      </div>
                      <span>You don't have a reminder scheduled.</span>
                    </v-tooltip>
                  </v-flex>
                </template>
              </td>

              <!-- Column: Actions -->
              <td data-td="actions">
                <v-layout align-center>
                  <v-menu
                    transition="slide-y-transition"
                    bottom
                    left
                    nudge-bottom="30"
                    nudge-left="10"
                    class="my-1"
                  >
                    <v-icon
                      slot="activator"
                      color="#0a1a25"
                      class="questionnaire-more-actions-btn"
                    >
                      more_vert
                    </v-icon>
                    <v-list class="questionnaire-action-menu">
                      <template v-if="props.item.is_draft">
                        <v-list-tile
                          @click="openQuestionnaireDialog(props.item)"
                        >
                          <v-list-tile-title> Edit Draft </v-list-tile-title>
                        </v-list-tile>
                      </template>
                      <template v-else>
                        <v-list-tile
                          @click="openEditQuestionnaire(props.item)"
                          v-if="!props.item.locked"
                        >
                          <v-list-tile-title>
                            Edit Reminders & Due Date
                          </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile
                          v-if="reminderSendable(props.item)"
                          @click="
                            openSendQuestionnaireReminderDialog(props.item)
                          "
                        >
                          <v-list-tile-title> Send Reminder </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile
                          @click="toggleLocked(props.item)"
                          v-if="props.item.locked"
                        >
                          <v-list-tile-title>
                            Unlock
                            {{
                              projectViewEnabled ? 'Project' : 'Questionnaire'
                            }}
                          </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile
                          @click="
                            lockQuestionnaireConfirmationDialogOpened = true;
                            selectedQuestionnaire = props.item;
                          "
                          v-else
                        >
                          <v-list-tile-title>
                            Lock
                            {{
                              projectViewEnabled ? 'Project' : 'Questionnaire'
                            }}
                          </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile
                          @click="openQuestionnaireExportDialog(props.item)"
                        >
                          <v-list-tile-title>
                            Export
                            {{
                              projectViewEnabled ? 'Project' : 'Questionnaire'
                            }}
                          </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile
                          @click="
                            generateQuestionnaireLink(
                              props.item.id,
                              props.item.company_id,
                            )
                          "
                        >
                          <v-list-tile-title>
                            Copy
                            {{
                              projectViewEnabled ? 'Project' : 'Questionnaire'
                            }}
                            Link
                          </v-list-tile-title>
                        </v-list-tile>
                      </template>

                      <v-list-tile
                        @click="openRemoveQuestionnaireDialog(props.item)"
                      >
                        <v-list-tile-title> Delete </v-list-tile-title>
                      </v-list-tile>
                    </v-list>
                  </v-menu>
                </v-layout>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-content>
</template>

<script>
import API from '@/api';
import { mapGetters, mapState } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import moment from 'moment';
import axios from 'axios';

import SmartQuestionnaireView from '@/components/SmartQuestionnaireView';
import ClientActivities from '@/components/ClientActivities';
import AllSignatureRequests from '@/components/AllSignatureRequests';
import AllTaxReturnDeliveries from '@/components/returns/AllTaxReturnDeliveries';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import ClientCard from './components/mobile/ClientCard';
import UtilsMixins from '@/mixins/UtilsMixins';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import AdminFunctionsMixin from '@/mixins/AdminFunctionsMixin';
import QuestionnaireMixin from '../../mixins/QuestionnaireMixin';
import ExportQuestionnaireDialog from '@/components/ExportQuestionnaireDialog';
import SendTemplateDialog from '@/components/dialogs/SendTemplateDialog';
import SendQuestionnaireDialog from '@/components/dialogs/SendQuestionnaireDialog';
import SendOpenItemsDialog from '@/components/dialogs/SendOpenItemsDialog';
import SendDocumentDropOffDialog from '@/components/dialogs/SendDocumentDropOffDialog';
import TemplateSchedulerDialog from '@/components/dialogs/TemplateSchedulerDialog';
import DisableEnableRemindersDialog from '@/components/dialogs/DisableEnableRemindersDialog';
import EditClientDialog from '@/components/dialogs/EditClientDialog';
import EditDueDateDialog from '@/components/dialogs/EditDueDateDialog';
import OverviewQuestionnairesTableClientNameDataCell from '@/components/tables/dataCells/OverviewQuestionnairesTableClientNameDataCell';
import SendQuestionnaireReminderDialog from '@/components/dialogs/SendQuestionnaireReminderDialog';
import UploadSignatureRequestDialog from '@/components/dialogs/UploadSignatureRequestDialog';
import UploadTaxReturnDialog from '@/components/returns/UploadTaxReturnDialog';
import NotificationSubscriptionDialog from '@/components/dialogs/NotificationSubscriptionDialog';
import SendCustomStatusChangeDialog from '@/components/dialogs/SendCustomStatusChangeDialog';
import SelectCustomStatusDialog from '@/components/dialogs/SelectCustomStatusDialog';
import CreateClientDialog from '@/components/dialogs/CreateClientDialog';
import EditEntityDialog from '@/components/dialogs/EditEntityDialog';
import BulkUpdateTagsDialog from '@/components/dialogs/BulkUpdateTagsDialog';
import ManageTagsDialog from '@/components/dialogs/ManageTagsDialog';
import SxPercentageIndicator from '@/components/sx/SxPercentageIndicator';
import SxNoteCount from '@/components/sx/SxNoteCount';
import SxCountBox from '@/components/sx/SxCountBox';
import SxTiny from '@/components/sx/SxTiny';
import SxIcon from '@/components/sx/SxIcon';
import SxFlex from '@/components/sx/SxFlex';
import AddTodoDialog from '@/components/dialogs/AddTodoDialog';
import TodoTasks from '@/components/TodoTasks';
import ImportTaxReturnDialog from '@/components/returns/ImportTaxReturnDialog';
import SelectTaxReturnMethodDialog from '@/components/returns/SelectTaxReturnMethodDialog';
import ClientImportDialog from '@/components/dialogs/ClientImportDialog';
import ReviewAndExportTable from '@/components/ReviewAndExportTable';
import {
  storeFilterRecord,
  resetStoredFilterTab,
  getStoredFilters,
  compareFilters,
} from '@/utils/overviewFilters';
import {
  defaultEntityTypes,
  otherCountries,
  preferredContactMethods,
  priorYearItemsImportMethods,
  progressList,
  rules,
  timeZones,
  yearOptions,
  taskStatus,
  states,
} from '@/constants/variables';
import sortBy from 'lodash/sortBy';
import { defaultOverviewFilterSet } from '@/constants/overviewFilters';

const OVERVIEW_ROW_OPTIONS = [
  {
    text: '10',
    value: 10,
  },
  {
    text: '50',
    value: 50,
  },
  {
    text: '100',
    value: 100,
  },
];

const OVERVIEW_STATUS_LIST = [
  {
    name: 'All',
    value: -1,
    description: 'All Questionnaire.',
  },
  {
    value: 'pending',
    name: 'Pending',
    description: "We're processing this Questionnaire.",
  },
  {
    value: 'sent',
    name: 'Sent',
    description: 'We sent out the Questionnaire.',
  },
  {
    value: 'opened',
    name: 'Opened',
    description: 'Your client opened this Questionnaire.',
  },
  {
    value: 'in_progress',
    name: 'In Progress',
    description: 'Your client has started working on the Questionnaire.',
  },
  {
    value: 'completed',
    name: 'Completed',
    description:
      'Your client completed all the questions in the questionnaire.',
  },
  {
    value: 'review_and_submit',
    name: 'Review & Submit',
    description: 'Questionnaire is in Review and Submit state.',
  },
  {
    value: 'draft',
    name: 'Draft',
    description: 'Questionnaire is a Draft.',
  },
];

export default {
  name: 'Overview',
  mixins: [
    UtilsMixins,
    AdminFunctionsMixin,
    FirmPreferenceMixins,
    QuestionnaireMixin,
  ],
  components: {
    ClientImportDialog,
    SendDocumentDropOffDialog,
    EditEntityDialog,
    CreateClientDialog,
    ClientCard,
    DisableEnableRemindersDialog,
    EditClientDialog,
    EditDueDateDialog,
    OverviewQuestionnairesTableClientNameDataCell,
    ClientActivities,
    ExportQuestionnaireDialog,
    ConfirmationDialog,
    ManageTagsDialog,
    BulkUpdateTagsDialog,
    SmartQuestionnaireView,
    SendTemplateDialog,
    SendQuestionnaireDialog,
    SendOpenItemsDialog,
    TemplateSchedulerDialog,
    SendQuestionnaireReminderDialog,
    AllSignatureRequests,
    AllTaxReturnDeliveries,
    UploadSignatureRequestDialog,
    ImportTaxReturnDialog,
    SelectTaxReturnMethodDialog,
    UploadTaxReturnDialog,
    NotificationSubscriptionDialog,
    SendCustomStatusChangeDialog,
    SelectCustomStatusDialog,
    SxPercentageIndicator,
    SxCountBox,
    SxNoteCount,
    SxTiny,
    SxFlex,
    SxIcon,
    AddTodoDialog,
    TodoTasks,
    ReviewAndExportTable,
  },
  data() {
    return {
      hasStoredFilters: false,
      timeZones,
      otherCountries,
      rules,
      yearOptions,
      isOnboarding: false,
      taskStatus,
      preferredContactMethods,
      priorYearItemsImportMethods,
      bulkUpdateTagsDialogOpened: false,
      savingQuestionnaire: false,
      hideInactive: false,
      selectedImportMethod: null,
      fullscreenDialog: false,
      clientView: 'users',
      newTemplateScheduler: false,
      sendQuestionnaireDialogOpened: false,
      sendQuestionnaireReminderDialogOpened: false,
      sendTemplateDialogOpened: false,
      sendOpenItemsDialogOpened: false,
      manageTagsDialogOpened: false,
      manageTagType: 'client-tags',
      settingsPaginationOptions: [
        { text: '10', value: 10 },
        { text: '50', value: 50 },
        { text: '100', value: 100 },
      ],
      active: 0,
      clientSearch: '',
      questionnaireSearch: '',
      entitySearch: '',
      selectedQuestionnaires: [],
      selectedClient: null,
      selectedEntity: null,
      selectedEntityName: '',
      selectEntityToEdit: false,
      selectedEntityIdsForEditingStatus: [],
      newClientFirstName: '',
      newClientLastName: '',
      newSpousePhoneNumber: '',
      newSpouseEmail: '',
      newType: '',
      newCompanyName: '',
      newClientPhoneNumber: '',
      addEntityDialogOpened: false,
      selectedYear: 'All',
      selectedOrganizerYear: 2024,
      addClientDialogOpened: false,
      editEntityInfoDialogOpened: false,
      selectClientDialogOpened: false,
      notificationSubscriptionDialogOpened: false,
      sendMultipleRemindersConfirmationDialogOpened: false,
      sendClientLoginInfoDialogOpened: false,
      deleteMultipleQuestionnairesConfirmationDialogOpened: false,
      questionnaireDialogOpened: false,
      selectedTaxSoftware: null,
      entityFilter: 'All',
      selectedAssignedUser: -1,
      selectedPreparerUser: -1,
      selectedClientManagerUser: -1,
      selectedSender: -1,
      selectedFirmGroup: -1,
      openItemsPreview: false,
      newOption: '',
      clientPagination: {
        sortBy: 'tax_return_name',
        rowsPerPage: 10,
        descending: false,
        page: 1,
        options: OVERVIEW_ROW_OPTIONS,
      },
      entityPagination: {
        sortBy: 'name',
        rowsPerPage: 10,
        descending: false,
        page: 1,
        options: OVERVIEW_ROW_OPTIONS,
      },
      questionnairePagination: {
        sortBy: 'last_activity',
        rowsPerPage: 10,
        descending: true,
        page: 1,
        options: OVERVIEW_ROW_OPTIONS,
      },
      newClientPreferredContactMethod: null,
      selectedClientTagsFilter: [],
      selectedEntityTagsFilter: [],
      advancedClient: false,
      mobileClientsHeaders: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'tax_return_name',
          width: '100%',
        },
      ],
      entityType: 'All',
      entityTypes: [
        'All',
        'Individual',
        'Corporation',
        'S-Corp',
        'Single Member LLC',
        'Partnership',
        'Estates & Trusts',
        'Other',
      ],
      newTypes: defaultEntityTypes,
      newEntityStreet: '',
      newEntityState: '',
      newEntityCountry: '',
      newEntityZip: '',
      questionnaireType: 'All',
      selectedClients: [],
      totalQuestionnaires: 0,
      allQuestionnaires: [],
      questionnaireStatus: -1,
      sendDocumentDropOffDialogOpened: false,
      removeQuestionnaireConfirmationDialogOpened: false,
      lockQuestionnaireConfirmationDialogOpened: false,
      lockQuestionnaireAfterExportConfirmationDialogOpened: false,
      firmUser: -1,
      firmUsers: [],
      tableLoading: false,
      engagementLetterIncluded: true,
      progressList,
      states: states,
      pageLoading: true,
      editClientInfoDialogOpened: false,
      removeClientConfirmationDialogOpened: false,
      removeEntityConfirmationDialogOpened: false,
      disableRemindersDialogOpened: false,
      editBulkDueDateDialogOpened: false,
      editDueDateDialogOpened: false,
      userImportDialogOpened: false,
      documentData: '',
      documentName: '',
      documentsUploaded: [],
      selectedQuestionnaire: null,
      selectedOpenItemQuestionnaire: null,
      remindersDisabled: false,
      spouseInfo: {
        fname: null,
        lname: null,
        date_of_birth: null,
        email: '',
        phone_number: '',
      },
      hasSpouse: false,
      questionnaireLoading: false,
      overviewOptionsDialogOpened: false,
      scheduleTemplate: false,
      followUp: false,
      newClientWorkPhoneNumber: '',
      newClientWorkPhoneExtension: '',
      newEntityBusinessEmail: '',
      newEntityOtherEmail: '',
      newEntityFax: '',
      currentUserFirm: null,
      uploadDialogOpened: false,
      selectedDocumentType: '',
      selectedQuestion: null,
      selectEntityDialogOpened: false,
      selectAssignmentDialogOpened: false,
      assignmentLoading: false,
      questionnaireScheduledDate: null,
      questionnaireScheduledTime: null,
      questionnaireDaysUntilDue: 7,
      templateSchedulerDialogOpened: false,
      schedulerLoading: false,
      newClientEin: '',
      newTag: '',
      suggestionsLoading: false,
      selectedAssignedAdvisorId: null,
      selectedCpaId: null,
      selectedClientManagerId: null,
      documentsToImport: [],
      refreshTries: 0,
      exportQuestionnaireDialogOpened: false,
      selectedQuestionnairesForExport: [],
      allQuestionnaireYears: [],
      clientActivitiesKey: 0,
      allActivities: [],

      selectedEntityIds: [],
      selectedEntities: [],
      refreshQuestionnairesWithDebounce: null,
      hasExpiredLinkedAccount: false,
      isPolling: false,

      addTodoDialogOpened: false,
      selectedTaskIds: [],
      allTodoTasks: [],
      sendCustomStatusChangeDialogOpened: false,
      newCustomStatus: null,
      selectFirmCustomStatusDialogOpened: false,
      selectedFirmCustomStatusFilter: [],

      // For signature requests
      signatureRequestTableLoading: false,
      uploadSignatureRequestDialogOpened: false,
      allSignatureRequests: [],

      // For tax return deliveries
      taxReturnDeliveryTableLoading: false,
      uploadTaxReturnDeliveryDialogOpened: false,
      allTaxReturns: [],
      selectTaxReturnMethodDialogOpened: false,
      importTaxReturnDialogOpened: false,

      // Questionnaire related
      questionnaireStatusList: OVERVIEW_STATUS_LIST,
      questionnaireStatusIncludeLocked: true,

      // Review & Export table
      startExportConfirmationDialogOpened: false,
      reviewAndExportYear: 2024,
      reviewAndExportSearch: '',
      entitiesWithTaxForms: [],
      reviewAndExportTotalItems: 0,
      reviewAndExportPagination: {
        sortBy: 'name',
        rowsPerPage: 5,
        descending: true,
        page: 1,

        options: [
          {
            text: '5',
            value: 5,
          },
          {
            text: '10',
            value: 10,
          },
          {
            text: '25',
            value: 25,
          },
        ],
      },
      getEntitiesWithTaxFormsWithDebounce: null,
      cancelTokenSourceCount: null,
      cancelTokenSourceData: null,
    };
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
      currentUserDefaultCompanyId: 'currentUserDefaultCompanyId',
      notifications: 'notifications',
      myConversations: 'myConversations',
    }),
    isClientView() {
      return Boolean(this.clientView === 'users');
    },
    selectedEntityCount() {
      return (this.selectedEntities && this.selectedEntities.length) || 0;
    },
    selectedClientCount() {
      return (this.selectedClients && this.selectedClients.length) || 0;
    },
    clientCount() {
      return (this.usersMapped && this.usersMapped.length) || 0;
    },
    entityCount() {
      return (this.companies && this.companies.length) || 0;
    },
    selectedCount() {
      return this.isClientView
        ? this.selectedClientCount
        : this.selectedEntityCount;
    },
    selectedQuestionnaireCount() {
      return this.selectedQuestionnaires.length || 0;
    },
    clientViewCount() {
      return this.isClientView ? this.clientCount : this.entityCount;
    },
    someClientsOrEntitiesNotSelected() {
      const companiesCounts = (this.companies && this.companies.length) || 0;
      const clientsCounts = (this.usersMapped && this.usersMapped.length) || 0;
      const hasSelected = this.isClientView
        ? this.selectedClientCount > 0
        : this.selectedEntityCount > 0;

      const isSomeNotSelected = this.isClientView
        ? clientsCounts > this.selectedClientCount
        : companiesCounts > this.selectedEntityCount;

      return isSomeNotSelected && hasSelected;
    },
    someQuestionnairesNotSelected() {
      const hasSelected = this.selectedQuestionnaireCount > 0;
      const isSomeNotSelected =
        this.totalQuestionnaires > this.selectedQuestionnaireCount;

      return isSomeNotSelected && hasSelected;
    },
    questionnaireHeaders() {
      return [
        {
          text: 'Client',
          align: 'left',
          sortable: true,
          value: 'client',
          width: '20%',
        },
        this.shouldShowExternalUserId
          ? {
              text: 'Entity ID',
              value: 'user.entity_id',
              align: 'left',
              width: '5%',
              sortable: true,
            }
          : undefined,
        {
          text: 'Year',
          align: 'left',
          sortable: false,
          value: 'year',
          width: '5%',
        },
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '20%',
        },
        {
          text: 'Status',
          align: 'left',
          sortable: true,
          value: 'status',
          width: '10%',
        },
        this.customStatusesEnabled
          ? {
              text: `Firm-internal Status`,
              align: 'left',
              sortable: true,
              width: '10%',
              value: 'firm_custom_status_id',
            }
          : undefined,
        {
          text: 'Notes',
          align: 'left',
          sortable: false,
          value: 'noteCount',
          width: '5%',
        },
        {
          text: 'Documents',
          align: 'left',
          sortable: false,
          value: 'documentCount',
          width: '5%',
        },
        {
          text: 'Activity',
          align: 'left',
          sortable: true,
          value: 'last_activity',
          width: '10%',
        },
        {
          text: 'Assigned to',
          align: 'left',
          sortable: false,
          value: 'assigned_advisor_id',
          width: '10%',
        },
        {
          text: 'Due Date',
          align: 'left',
          sortable: true,
          value: 'due_date',
          width: '10%',
        },
        this.currentUser.overview_setting.show_next_reminder_date && {
          text: 'Next Reminder',
          align: 'left',
          sortable: true,
          value: 'next_reminder',
          width: '5%',
        },
        {
          text: 'Actions',
          align: 'left',
          sortable: false,
          width: '5%',
        },
      ].filter(Boolean);
    },
    clientsHeaders() {
      return [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'tax_return_name',
          width: '28%',
        },
        {
          text: 'Email',
          align: 'left',
          sortable: true,
          value: 'email',
          width: '20%',
        },
        {
          text: 'Phone number',
          value: 'phone_number',
          align: 'left',
          sortable: false,
          width: '15%',
        },
        this.currentUser.overview_setting.use_contact_tags
          ? {
              text: 'Client Tags',
              value: 'user.contact_tags',
              align: 'left',
              width: '8%',
              sortable: false,
            }
          : undefined,
        this.useFirmRoles
          ? {
              text: 'Preparer',
              align: 'left',
              sortable: true,
              value: 'cpa_id',
              width: '8%',
            }
          : undefined,
        this.useFirmRoles
          ? {
              text: 'Client Manager',
              align: 'left',
              sortable: true,
              value: 'client_manager_id',
              width: '8%',
            }
          : undefined,
        {
          text: 'Assigned to',
          align: 'left',
          sortable: false,
          value: 'assigned_advisor_id',
          width: '30%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '5%',
        },
      ].filter(Boolean);
    },
    entityViewHeaders() {
      const withTags =
        this.currentUser.overview_setting.use_contact_tags ||
        this.currentUser.overview_setting.use_entity_tags;

      const headers = [
        {
          text: 'Name (Click for detail)',
          align: 'left',
          sortable: true,
          value: 'name',
          width: withTags ? '20%' : '25%',
        },
        {
          text: 'Entity Type',
          align: 'left',
          sortable: true,
          value: 'entity_type',
          width: withTags ? '13%' : '15%',
        },
        {
          text: 'Client Name',
          align: 'left',
          sortable: true,
          value: 'client_name',
          width: withTags ? '18%' : '25%',
        },
        this.currentUser.overview_setting.use_contact_tags
          ? {
              text: 'Client Tags',
              value: 'contact_tags',
              align: 'left',
              width: '10%',
              sortable: false,
            }
          : undefined,
        this.currentUser.overview_setting.use_entity_tags
          ? {
              text: 'Entity Tags',
              value: 'entity_tags',
              align: 'left',
              width: '10%',
              sortable: false,
            }
          : undefined,
        {
          text: 'Assigned to',
          align: 'left',
          sortable: false,
          value: 'assigned_advisor_id',
          width: withTags ? '25%' : '20%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '5%',
        },
      ].filter(Boolean);

      return headers.filter(h => h);
    },
    shouldShowExternalUserId() {
      return (
        this.externalUserIdAllowed &&
        this.currentUser.overview_setting &&
        this.currentUser.overview_setting.show_external_user_id
      );
    },
    firmGroups() {
      return this.currentUserFirm
        ? sortBy(this.currentUserFirm.firm_groups, 'name')
        : [];
    },
    supportedImportMethods() {
      return this.priorYearItemsImportMethods
        .slice(1)
        .filter(a => a.bulk_supported);
    },
    showGroupFilters() {
      return (
        ['full', 'partner'].includes(this.currentUser.firm_access) &&
        this.hasFirmGroups
      );
    },
    allowAssignInGroup() {
      return this.currentUser.firm_access === 'group' && this.hasFirmGroups;
    },
    firmContactTags() {
      return this.currentUserFirm
        ? this.currentUserFirm.contact_tags.map(t => t.name)
        : [];
    },
    firmEntityTags() {
      return this.currentUserFirm
        ? this.currentUserFirm.entity_tags.map(t => t.name)
        : [];
    },
    firmContactTagsWithNone() {
      return this.firmContactTags.concat('(no tags)');
    },
    firmEntityTagsWithNone() {
      return this.firmEntityTags.concat('(no tags)');
    },
    duplicateExistenceRules() {
      return [
        v => !!v || 'This field is required',
        v =>
          this.isEmpty(
            this.companies.filter(
              c => c.name.toLowerCase() === v.toLowerCase(),
            ),
          ) || 'Duplicate Entity Detected',
        v =>
          !v.includes('/') || "You cannot have slash (/) in the entity's name",
      ];
    },
    firmCustomStatusesWithNone() {
      if (this.currentUserFirm) {
        return [
          {
            id: 'null',
            firm_user_display_status: 'No Status',
          },
          ...this.currentUserFirm.firm_custom_statuses,
        ];
      }
    },
    entitiesWithAll() {
      return ['All', ...[...new Set(this.companies.map(c => c.entity_type))]];
    },
    yearOptionsFromPastQuestionnaires() {
      return [
        'All',
        ...uniq(this.yearOptions.concat(this.allQuestionnaireYears)),
      ];
    },
    mappedYearOptionsForDefaultYear() {
      return [
        {
          text: 'All',
          value: -1,
        },
        ...this.yearOptions.map(y => ({
          text: y.toString(),
          value: y,
        })),
      ];
    },
    sortRuleOptions() {
      return [
        {
          text: 'None',
          value: 'none',
        },
        {
          text: 'Smart Priority',
          value: 'smart_priority',
        },
      ];
    },
    questionnairesMapped() {
      return this.allQuestionnaires.map(questionnaire => {
        const company = this.companies.find(
          c => c.id === questionnaire.company_id,
        );
        const hash = company
          ? company.name
              .replaceAll(' ', '-')
              .replaceAll(',', '')
              .replaceAll('#', '') +
            '-' +
            company.hash_id
          : null;

        const entity = this.getEntity(questionnaire);
        var client = null;

        if (entity) {
          client = this.isIndividual(entity)
            ? `${entity.tax_return_name}`
            : `${entity.name} (${entity.user.full_name})`;
        }
        const hasFirmCustomStatuses = Boolean(
          this.currentUserFirm && this.currentUserFirm.firm_custom_statuses,
        );
        const firm_custom_status =
          hasFirmCustomStatuses && this.customStatusesEnabled
            ? this.currentUserFirm.firm_custom_statuses.find(
                firm_custom_status =>
                  firm_custom_status.id === questionnaire.firm_custom_status_id,
              )
            : null;

        const documentTotal = questionnaire.document_count_total;
        const validSections = questionnaire.sections.filter(
          s => s.section_key !== 'document_checklist',
        );
        const sectionCount = validSections.length;
        const sectionDoneCount = validSections.filter(a => a.marked_completed)
          .length;
        const visibleNotes = questionnaire.notes.filter(note => !note.removed);

        const currentSection =
          questionnaire.sections &&
          questionnaire.sections.find(
            section => section.id === questionnaire.status,
          );
        const currentStep = currentSection
          ? currentSection.name
          : 'In Progress';

        return {
          name: questionnaire.name,
          sectionCount,
          sectionDoneCount,
          documentTotal: documentTotal,
          documentCount: Math.min(
            documentTotal,
            questionnaire.document_count_answered,
          ),
          client,
          company,
          externalUserId: company.user.external_user_id,
          hash,
          firm_custom_status,
          ...this.objectWithoutProperties(questionnaire, ['name']),
          notes: visibleNotes,
          currentStep,
        };
      });
    },
    isExternalIdvisible() {
      const isExternalIdvisible = Boolean(
        this.currentUserFirm.preferences.external_user_id_allowed &&
          this.currentUser.overview_setting.show_external_user_id,
      );

      return isExternalIdvisible;
    },
    usersMapped() {
      return map(
        groupBy(this.companies, company => company.user.id),
        companies => {
          const fistCompanyItem = companies[0];
          const user = fistCompanyItem.user;
          const tax_return_name =
            companies.length === 1 && this.isIndividual(fistCompanyItem)
              ? fistCompanyItem.tax_return_name
              : companies.length === 1 && !this.isIndividual(fistCompanyItem)
              ? `${user.tax_return_name} (${fistCompanyItem.name})`
              : user.tax_return_name;
          const search_name =
            user.tax_return_name +
            ' ' +
            companies.map(c => c.tax_return_name).join(' ');

          return {
            id: user.id,
            full_name: user.full_name,
            search_name,
            tax_return_name,
            fname: user.fname,
            lname: user.lname,
            email: user.email,
            preferred_contact_method: user.preferred_contact_method,
            phone_number: user.phone_number,
            companies,
            assigned_advisor_id: user.assigned_advisor_id,
            passwordless: user.passwordless,
            contact_tags: user.contact_tags,
            time_zone: user.time_zone,
            paying: user.paying,
            for_testing: user.for_testing,
            created_at: user.created_at,
            firm_group: user.firm_group,
            external_entity_id: fistCompanyItem.external_entity_id || '',
            external_user_id: user.external_user_id || '',
            has_temp_email: get(user, 'preferences.has_temp_email', false),
            readonly_access: user.readonly_access,
            cpa_id: user.cpa_id,
            client_manager_id: user.client_manager_id,
            preferred_sign_in_method: user.preferred_sign_in_method,
          };
        },
      )
        .filter(
          c =>
            !this.currentUser.overview_setting.show_assigned_clients ||
            c.assigned_advisor_id === this.currentUser.id,
        )
        .filter(c => !this.hideInactive || c.paying);
    },

    entitiesMapped() {
      return this.companies
        .map(company => {
          return {
            id: company.id,
            name: company.tax_return_name,
            tax_return_name: company.tax_return_name,
            entity_type: company.entity_type,
            client_name: company.user.full_name,
            relevant_conversation_id: company.relevant_conversation_id,
            assigned_advisor_id: company.user.assigned_advisor_id,
            inactive: company.inactive,
            readonly_access: company.readonly_access,
            for_testing: company.user.for_testing,
            created_at: company.created_at,
            user: company.user,
            entity_tags: company.entity_tags,
            contact_tags: company.user.contact_tags,
            other_companies: this.companies.filter(
              c => c.user_id === company.user_id,
            ),
            notification_subscribed: company.notification_subscribed,
            hash_id: company.hash_id,
            hash: company
              ? company.name
                  .replaceAll(' ', '-')
                  .replaceAll(',', '')
                  .replaceAll('#', '') +
                '-' +
                company.hash_id
              : null,
            spouse_info: company.spouse_info,
            firm_group: company.user.firm_group,
            external_entity_id: company.external_entity_id || '',
            external_user_id: company.user.external_user_id || '',
          };
        })
        .filter(
          c =>
            !this.currentUser.overview_setting.show_assigned_clients ||
            c.assigned_advisor_id === this.currentUser.id,
        )
        .filter(c => !this.hideInactive || (c.user.paying && !c.inactive));
    },
    newEntityFilledOut() {
      return (
        this.selectedClient &&
        this.newType &&
        ((this.newType === 'Individual' &&
          this.newClientFirstName &&
          this.newClientLastName) ||
          (this.newCompanyName && !this.newCompanyName.includes('/')))
      );
    },
    questionnaireSavable() {
      return (
        !this.selectedQuestionnaire.due_date ||
        moment() < moment(this.selectedQuestionnaire.due_date)
      );
    },
    questionnaireTypes() {
      return ['All', 'Templates', 'Custom'];
    },
    spouseSavable() {
      if (this.newType === 'Individual' && this.newClientHasSpouse) {
        return !isEmpty(this.newSpouseFirstName);
      }
      return true;
    },
    selectedQuestionnairesWithoutDrafts() {
      return this.selectedQuestionnaires.filter(q => !q.is_draft);
    },
    selectedQuestionnairesThatCanBeReminded() {
      return this.selectedQuestionnaires.filter(
        q => !q.completed && !q.locked && !q.is_draft,
      );
    },
    selectedQuestionnairesThatCanBeLocked() {
      return this.selectedQuestionnaires.filter(q => !q.locked && !q.is_draft);
    },
    isPartnerOrFullUser() {
      return ['partner', 'full'].includes(this.currentUser.firm_access);
    },
    selectedTasks() {
      return this.allTodoTasks.filter(task =>
        this.selectedTaskIds.includes(task.id),
      );
    },
  },
  methods: {
    get,
    storeFilterRecord,
    sortBy,

    // async startExport() {
    //   this.startExportConfirmationDialogOpened = false;
    //   try {
    //     const response = await API.integration.runAllFirmExportTasks({
    //       firmId: this.selectedFirmId,
    //     });
    //     this.$store.dispatch('application/setSuccessNotification', {
    //       message: "Soraban Bot will begin the export tasks you've scheduled.",
    //     });
    //   } catch (e) {
    //     console.log(e);
    //     this.$store.dispatch('application/setErrorNotification', {
    //       message:
    //         'Export to Tax Software is currently unavailable. Please reach out to support if you need it immediately.',
    //     });
    //   }
    // },

    async getEntitiesWithTaxForms() {
      try {
        this.tableLoading = true;

        const params = {
          year: this.reviewAndExportYear,
          page: this.reviewAndExportPagination.page,
          rows_per_page: this.reviewAndExportPagination.rowsPerPage,
          sort_by: this.reviewAndExportPagination.sortBy,
          descending: this.reviewAndExportPagination.descending,
          search_term: this.reviewAndExportSearch,
        };

        const getCountResponse = await API.reviewAndExport.getEntitiesWithTaxForms(
          {
            ...params,
            count: true,
          },
        );

        const { data } = getCountResponse;

        this.reviewAndExportTotalItems = data.count;
        this.reviewAndExportPagination.totalItems = this.reviewAndExportTotalItems;

        const response = await API.reviewAndExport.getEntitiesWithTaxForms(
          params,
        );

        this.entitiesWithTaxForms = response.data;
      } finally {
        this.tableLoading = false;
      }
    },

    async toggleTrackChanges(trackChange) {
      const response = await API.questionnaire.saveQuestionnaire({
        company_id: this.selectedQuestionnaire.company_id,
        id: this.selectedQuestionnaire.id,
        track_changes: trackChange,
      });
      this.selectedQuestionnaire.track_changes = !this.selectedQuestionnaire
        .track_changes;
      let cachedQuestionnaire = this.allQuestionnaires.find(
        q => q.id === response.data.id,
      );
      Object.assign(cachedQuestionnaire, response.data);
      this.lockQuestionnaireAfterExportConfirmationDialogOpened = false;
      this.$forceUpdate();
    },
    selectAllClientsOrEntities() {
      if (this.isClientView) {
        this.selectedClients = this.usersMapped;
      } else {
        this.selectedEntities = this.companies;
      }
    },
    clearFilters() {
      const tabName = this.getFilterTabName();
      resetStoredFilterTab(tabName);

      this.questionnairePagination.rowsPerPage = 10;
      this.questionnairePagination.options = OVERVIEW_ROW_OPTIONS;

      this.selectedQuestionnaires = [];
      this.hasStoredFilters = false;
      this.updateLocalFilterState();
    },
    async checkStoredFilters() {
      const filters = getStoredFilters();
      const tabName = this.getFilterTabName();
      const hasStoredFilters = compareFilters(
        filters[tabName],
        defaultOverviewFilterSet[tabName],
      );

      this.hasStoredFilters = hasStoredFilters;
      this.updateLocalFilterState(filters);
    },
    getFilterTabName() {
      if (this.active === 0) return 'overview';
      if (this.active === 1 && this.clientView === 'users') return 'clients';

      return 'entities';
    },
    updateLocalFilterState(incomingFilters = {}) {
      const safeFilters = {
        ...defaultOverviewFilterSet,
        ...(incomingFilters || {}),
      };
      const tabName = this.getFilterTabName();

      this.entityFilter = safeFilters[tabName].entityFilter;
      this.hideInactive = safeFilters[tabName].hideInactive;
      this.questionnaireStatus = safeFilters[tabName].questionnaireStatus;
      this.questionnaireStatusIncludeLocked =
        safeFilters[tabName].questionnaireStatusIncludeLocked;
      this.questionnaireType = safeFilters[tabName].questionnaireType;
      this.selectedAssignedUser = safeFilters[tabName].selectedAssignedUser;
      this.selectedClientManagerUser =
        safeFilters[tabName].selectedClientManagerUser;
      this.selectedClientTagsFilter =
        safeFilters[tabName].selectedClientTagsFilter;
      this.selectedEntityTagsFilter =
        safeFilters[tabName].selectedEntityTagsFilter;
      this.selectedFirmCustomStatusFilter =
        safeFilters[tabName].selectedFirmCustomStatusFilter;
      this.selectedFirmGroup = safeFilters[tabName].selectedFirmGroup;
      this.selectedPreparerUser = safeFilters[tabName].selectedPreparerUser;
      this.selectedSender = safeFilters[tabName].selectedSender;
      this.selectedYear = safeFilters[tabName].selectedYear;
    },
    updateFilterRecord(record, value) {
      const tabName = this.getFilterTabName();

      storeFilterRecord(tabName, record, value);
      this.checkStoredFilters();
    },
    async openSendCustomStatusChangeDialog(questionnaire, newCustomStatus) {
      this.selectedQuestionnaires = [questionnaire];
      this.newCustomStatus = newCustomStatus;
      questionnaire.firm_custom_status_id = newCustomStatus
        ? newCustomStatus.id
        : null;
      await this.saveQuestionnaireCustomStatus(questionnaire);
      this.refreshQuestionnaires();
      this.sendCustomStatusChangeDialogOpened = true;
    },
    openChangeMultipleFirmCustomStatusesDialog() {
      this.newCustomStatus = null;
      this.selectFirmCustomStatusDialogOpened = true;
    },
    getDocumentNeededCount(documentsNeeded = []) {
      const requiredDocuments = documentsNeeded.filter(d => !d[2]);
      const requiredDocumentCount = requiredDocuments.length;

      return requiredDocumentCount;
    },
    getRequiredDocumentName(document) {
      const isNameEmpty = isEmpty(document[0]);
      const documentName = this.truncateString(
        this.removeHtml(isNameEmpty ? document[1] : document[0].join('/')),
        50,
      );
      const isOptional = Boolean(document && document[2]);

      // Construct string
      const optionalString = isOptional ? ' (Optional)' : '';
      const cleanString = documentName
        .replace('Please upload ', '')
        .replace('Please provide ', '')
        .replace('Upload ', '');

      return cleanString + optionalString;
    },
    async selectCustomStatus(customStatus) {
      this.newCustomStatus = customStatus;
      this.tableLoading = true;
      await Promise.all(
        this.selectedQuestionnaires.map(async questionnaire => {
          questionnaire.firm_custom_status_id = customStatus
            ? customStatus.id
            : null;
          await this.saveQuestionnaireCustomStatus(questionnaire);
        }),
      );
      this.refreshQuestionnaires();
      this.selectFirmCustomStatusDialogOpened = false;
      this.sendCustomStatusChangeDialogOpened = true;
    },
    async generateQuestionnaireLink(id, companyId) {
      const response = await API.questionnaire.generateQuestionnaireLink({
        id,
        companyId,
      });
      this.copyTextToClipboard(response.data.link);
      this.$store.dispatch('application/setSuccessNotification', {
        message: 'Questionnaire link was copied to clipboard.',
      });
    },
    async generateQuestionnaireReport() {
      this.$store.dispatch('application/setSuccessNotification', {
        message:
          'Your report is being generated. This usually takes less than a minute to complete.',
      });
      const response = await API.report.generateQuestionnaireReport(
        this.selectedYear,
      );
      this.saveAs(
        response.data,
        `${this.currentUser.full_name}_questionnaire_report_for_${this.selectedYear}.xls`,
      );
    },
    saveAs(uri, filename) {
      var link = document.createElement('a');
      if (typeof link.download === 'string') {
        link.href = uri;
        link.download = filename;
        // Firefox requires the link to be in the body
        document.body.appendChild(link);
        // simulate click
        link.click();
        // remove the link when done
        document.body.removeChild(link);
      } else {
        window.open(uri, '_blank');
      }
    },
    getFirmUserName(id) {
      const firmUser = this.firmUsers.find(user => user.id === id);
      return firmUser && firmUser.name ? firmUser.name : '';
    },
    getFirmUsersFilterOptions() {
      let users = this.allowAssignInGroup
        ? this.filterFirmUsers()
        : this.firmUsers;
      users = sortBy(users, ['name']);
      return [
        {
          name: 'All',
          id: -1,
        },
        ...users,
      ];
    },
    viewParentQuestionnaire() {
      this.$router.push(
        `/questionnaire?companyId=${this.selectedQuestionnaire.company_id}&questionnaireId=${this.selectedQuestionnaire.id}`,
      );
    },
    filterFirmUsers(assignedId, clientGroup) {
      if (this.isPartnerOrFullUser) {
        // Full, partners or Users in clients group
        return this.firmUsers.filter(user => {
          return (
            ['partner', 'full', 'basic'].includes(user.access) ||
            (user.group && clientGroup && user.group.id === clientGroup.id)
          );
        });
      } else {
        // Assigned user or in same group
        return this.firmUsers.filter(user => {
          return (
            (assignedId && user.id === assignedId) ||
            (user.group && user.group.id === this.currentUser.firm_group.id)
          );
        });
      }
    },
    openSendQuestionnaireDialog() {
      this.selectedEntityIds = this.selectedEntities
        .filter(e => !e.inactive)
        .map(e => e.id);
      this.sendQuestionnaireDialogOpened = true;
    },
    // NOTE from Jordan: using this approach because when there are more than 100 questionnaires,
    // we often can't load them all within 30 seconds (the Heroku default timeout). So we're instead
    // asking for 50 at a time and then combining them together after.
    async selectAllQuestionnaires() {
      this.tableLoading = true;

      const params = this.buildGetQuestionnairesParams();
      const rowsPerPage = 50;

      async function getQuestionnaires(page) {
        params.page = page;
        params.rows_per_page = rowsPerPage;
        const response = await axios.get('/v2/questionnaires', { params });
        return response.data;
      }

      const totalPages = Math.ceil(this.totalQuestionnaires / rowsPerPage);
      const promises = [];

      for (let i = 1; i <= totalPages; i++) {
        promises.push(async () => await getQuestionnaires(i));
      }

      // We'd only want to, max, ask for 200 (50 * 4) at a time
      const batchSize = 4;

      async function runBatch(startOfBatch) {
        const batchPromises = promises.slice(
          startOfBatch,
          startOfBatch + batchSize,
        );
        const batchOfQuestionnaires = await Promise.all(
          batchPromises.map(p => p()),
        );
        return batchOfQuestionnaires.flatten();
      }

      const batchPromises = [];

      for (let i = 0; i < promises.length; i += batchSize) {
        batchPromises.push(async () => await runBatch(i));
      }

      const questionnaires = [];

      await (async function loop() {
        for (let i = 0; i < batchPromises.length; i++) {
          const result = await batchPromises[i]();
          questionnaires.push(result);
        }
      })();

      this.allQuestionnaires = questionnaires.flatten();
      this.selectedQuestionnaires = this.questionnairesMapped;

      this.tableLoading = false;
    },
    openNotificationSubscriptionDialog(id) {
      this.selectedEntityIds = id ? [id] : this.selectedEntities.map(e => e.id);
      this.notificationSubscriptionDialogOpened = true;
    },
    handleNotification(activity, openInNewWindow) {
      this.$emit('handleNotification', activity, openInNewWindow);
    },
    objectWithoutProperties(obj, keys) {
      let target = {};
      for (const i in obj) {
        if (keys.indexOf(i) >= 0) continue;
        if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
        target[i] = obj[i];
      }
      return target;
    },
    openSendQuestionnaireReminderDialog(questionnaire) {
      this.selectedQuestionnaires = [questionnaire];
      this.sendQuestionnaireReminderDialogOpened = true;
    },
    reminderSendable(questionnaire) {
      return !questionnaire.completed && !questionnaire.locked;
    },
    createCustomQuestionnaire() {
      this.sendOpenItemsDialogOpened = true;
    },
    createTemplateScheduler() {
      this.newTemplateScheduler = true;
      this.sendTemplateDialogOpened = true;
      this.templateSchedulerDialogOpened = false;
    },
    showEntityInformation(client) {
      return (
        client.companies.filter(c => c.entity_type === 'Individual').length > 2
      );
    },
    oneIndividualEntitiesExist(entity) {
      let companies = this.companies.filter(c => c.user.id === entity.user.id);
      return (
        companies.length === 1 && companies[0].entity_type === 'Individual'
      );
    },
    openQuestionnaireExportDialog(questionnaire) {
      this.questionnaireDialogOpened = false;
      this.selectedQuestionnairesForExport = [questionnaire];
      this.selectedQuestionnaire = questionnaire;
      this.exportQuestionnaireDialogOpened = true;
    },
    openSelectedQuestionnairesForExport() {
      this.selectedQuestionnairesForExport = this.selectedQuestionnairesWithoutDrafts;
      this.exportQuestionnaireDialogOpened = true;
    },
    openLockQuestionnaireConfirmationDialog() {
      this.lockQuestionnaireAfterExportConfirmationDialogOpened = true;
    },
    individualEntity(client) {
      return client.companies.find(c => c.entity_type === 'Individual');
    },
    firmContactTagsAndSelf(user) {
      if (this.currentUserFirm && user) {
        return this.currentUserFirm.contact_tags
          .concat(...user.contact_tags)
          .filter(this.onlyUnique);
      } else {
        return [];
      }
    },
    firmEntityTagsAndSelf(company) {
      if (this.currentUserFirm && company) {
        return this.currentUserFirm.entity_tags
          .concat(...company.entity_tags)
          .filter(this.onlyUnique);
      } else {
        return [];
      }
    },
    duplicateRules(value) {
      if (
        !this.isEmpty(
          this.companies.filter(
            c => c.name.toLowerCase() === value.toLowerCase(),
          ),
        )
      ) {
        return 'Duplicate Entity Detected';
      }
    },
    openSelectEntityDialogToEdit(client) {
      this.selectedClient = client;
      this.selectedEntity = null;
      this.selectEntityToEdit = true;
      this.selectEntityDialogOpened = true;
    },
    openSelectEntityDialogToDelete(client) {
      this.selectedClient = client;
      this.selectedEntity = null;
      this.selectEntityToEdit = false;
      this.selectEntityDialogOpened = true;
    },
    async sendClientSignInLink(user_id) {
      try {
        const response = await API.advisor.sendClientLoginInfo({
          userIds: [user_id],
        });
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
            errorToSend: response.data.error,
            userEmail: this.currentUser.email,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Sent sign-in link!',
          });
        }
      } catch (e) {
        console.log(e);
      }
    },
    async sendClientLoginInfo() {
      try {
        const response = await API.advisor.sendClientLoginInfo({
          userIds: this.selectedClients.map(u => u.id),
        });
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
            errorToSend: response.data.error,
            userEmail: this.currentUser.email,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Sent sign-in link!',
          });
          this.sendClientLoginInfoDialogOpened = false;
          this.selectedClients = [];
        }
      } catch (e) {
        console.log(e);
      }
    },
    async saveOverviewSetting() {
      try {
        const response = await API.user.saveOverviewSetting(
          this.currentUser.overview_setting,
        );
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
            errorToSend: response.data.error,
            userEmail: this.currentUser.email,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Updated.',
          });
          this.$store.dispatch('getCurrentUser');
          this.overviewOptionsDialogOpened = false;

          this.questionnairePagination.page = 1;
          this.refreshTable();
        }
      } catch (e) {
        console.log(e);
      }
    },
    openEditQuestionnaire(questionnaire) {
      this.selectedQuestionnaire = Object.assign({}, questionnaire);
      this.remindersDisabled = this.selectedQuestionnaire.reminders_disabled;
      this.editDueDateDialogOpened = true;
    },
    async saveRemindersDisabled() {
      this.savingQuestionnaire = true;
      const response = await API.questionnaire.saveQuestionnaire({
        company_id: this.selectedQuestionnaire.company_id,
        id: this.selectedQuestionnaire.id,
        due_date: this.selectedQuestionnaire.due_date,
        reminders_disabled: this.remindersDisabled,
      });
      if (response.data.error) {
        console.log(response.data.error);
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
          errorToSend: response.data.error,
          userEmail: this.currentUser.email,
        });
        this.savingQuestionnaire = false;
      } else {
        this.refreshQuestionnaires();
        this.$forceUpdate();
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Updated.',
        });
        this.savingQuestionnaire = false;
        this.editDueDateDialogOpened = false;
      }
    },
    getHighlightedClass(record, value) {
      const isArray = Array.isArray(value);
      const tabName = this.getFilterTabName();
      const filters = getStoredFilters();

      const isChanged = isArray
        ? filters[tabName][record].length !== value.length
        : filters[tabName][record] !== value;

      return isChanged ? 'filter-border--active' : 'filter-border--clear';
    },
    async saveQuestionnaireCustomStatus(questionnaire) {
      const response = await API.questionnaire.saveQuestionnaire({
        company_id: questionnaire.company_id,
        id: questionnaire.id,
        firm_custom_status_id: questionnaire.firm_custom_status_id,
      });
      if (response.data.error) {
        console.log(response.data.error);
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
          errorToSend: response.data.error,
          userEmail: this.currentUser.email,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Updated.',
        });
      }
    },
    async saveQuestionnaire() {
      this.savingQuestionnaire = true;
      const response = await API.questionnaire.saveQuestionnaire({
        company_id: this.selectedQuestionnaire.company_id,
        id: this.selectedQuestionnaire.id,
        due_date: this.selectedQuestionnaire.due_date,
        reminders_disabled: this.selectedQuestionnaire.reminders_disabled,
      });
      if (response.data.error) {
        console.log(response.data.error);
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
          errorToSend: response.data.error,
          userEmail: this.currentUser.email,
        });
        this.savingQuestionnaire = false;
      } else {
        let questionnaire = this.allQuestionnaires.find(
          q => q.id === response.data.id,
        );
        Object.assign(questionnaire, response.data);
        this.$forceUpdate();
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Updated.',
        });
        this.savingQuestionnaire = false;
        this.editDueDateDialogOpened = false;
      }
    },
    async downloadClientImportTemplate() {
      try {
        const response = await API.advisor.downloadClientImportTemplate();
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          window.open(response.data.document.data.link, '_blank');
        }
      } catch (e) {
        console.log(e);
      }
    },
    async lockMultipleQuestionnaires() {
      try {
        const response1 = await API.questionnaire.lockQuestionnaires({
          questionnaireIds: this.selectedQuestionnaires
            .filter(q => !q.locked)
            .map(q => q.id),
        });
        if (response1.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response1.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'You have locked the questionnaires.',
          });
          this.selectedQuestionnaires
            .filter(q => !q.locked)
            .forEach(questionnaire => {
              questionnaire.locked = !questionnaire.locked;
            });
          this.selectedQuestionnaires = [];
          this.refreshTable();
        }
      } catch (e) {
        console.log(e);
      }
    },
    editOpenItems(questionnaire) {
      this.selectedQuestionnaire = Object.assign({}, questionnaire);
      this.selectedOpenItemQuestionnaire = Object.assign({}, questionnaire);
      this.sendOpenItemsDialogOpened = true;
    },
    async openQuestionnaireByCompanyIdAndQuestionnaireId(
      companyId,
      questionnaireId,
    ) {
      try {
        this.questionnaireDialogOpened = true;
        this.questionnaireLoading = true;
        const response = await API.questionnaire.getQuestionnaire({
          companyId: companyId,
          id: questionnaireId,
        });
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
          this.questionnaireDialogOpened = false;
        } else {
          this.selectedQuestionnaire = response.data;
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong. Please refresh the page.',
        });
      } finally {
        this.questionnaireLoading = false;
      }
    },
    async openQuestionnaireDialog(questionnaire) {
      if (questionnaire.processing) {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Please wait until the questionnaire is created.',
        });
      } else {
        try {
          if (questionnaire.is_draft && questionnaire.is_open_items) {
            this.editOpenItems(questionnaire);
          } else {
            this.questionnaireLoading = true;
            this.selectedQuestionnaire = questionnaire; // to use previously loaded questionnaire information (limited) before the questionnaire loads
            this.questionnaireDialogOpened = true;
            const response = await API.questionnaire.getQuestionnaire({
              companyId: questionnaire.company_id,
              id: questionnaire.id,
            });
            this.selectedQuestionnaire = response.data;
            this.selectedEntityName = questionnaire.client; // questionnaire has different response than getQuestionnaire call response
            this.questionnaireLoading = false;
          }
        } catch (e) {
          console.log(e);
        }
      }
    },
    async refreshQuestionnaireDialog(questionnaire, done) {
      try {
        if (questionnaire.is_draft && questionnaire.is_open_items) {
          this.editOpenItems(questionnaire);
        } else {
          this.selectedQuestionnaire = questionnaire;
          const response = await API.questionnaire.getQuestionnaire({
            companyId: questionnaire.company_id,
            id: questionnaire.id,
          });
          this.selectedQuestionnaire = response.data;
          this.questionnaireLoading = false;

          if (done) {
            done();
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    closeQuestionnaireDialog() {
      this.questionnaireDialogOpened = false;

      if (this.$route.query.companyId) {
        this.$router.push('/overview');
      }
    },
    isIndividual(entity) {
      return entity ? entity.entity_type === 'Individual' : false;
    },
    getEntity(questionnaire) {
      if (questionnaire) {
        return this.companies.find(c => c.id === questionnaire.company_id);
      }
    },
    isEmpty,
    moment,
    hasNewMessageForUser(user_id) {
      return this.myConversations.some(c =>
        c.unseen_messages.some(
          m =>
            m.user_id === user_id &&
            m.unseen_user_ids.includes(this.currentUser.id),
        ),
      );
    },
    hasNewMessageInRelevantConversation(company) {
      let relevantConversation = this.myConversations.find(
        c => c.id === company.relevant_conversation_id,
      );
      if (relevantConversation) {
        return relevantConversation.unseen_messages.some(
          m =>
            m.user_id === user_id &&
            m.unseen_user_ids.includes(this.currentUser.id),
        );
      }
    },
    isLoading(questionnaire) {
      const date = moment(questionnaire.created_at);
      return date.add(5, 'seconds') > moment() || questionnaire.processing;
    },
    openEditClientInfo(client) {
      this.selectedClient = null;
      this.selectedClient = Object.assign({}, client);
      if (get(this.selectedClient, 'preferences.has_temp_email', false)) {
        this.selectedClient.email = null;
      }
      this.editClientInfoDialogOpened = true;
    },
    openEditEntityDialog(entity) {
      this.selectedEntity = null;
      this.selectedEntity = Object.assign({}, entity);
      this.selectedClient = Object.assign({}, entity.user);
      this.editEntityInfoDialogOpened = true;
    },
    openAddClientDialog() {
      this.addClientDialogOpened = true;
    },
    openUploadSignatureRequestDialog() {
      this.uploadSignatureRequestDialogOpened = true;
    },
    openUploadTaxReturnDialog() {
      this.uploadTaxReturnDeliveryDialogOpened = true;
    },
    openAddEntityDialog(client) {
      this.selectedClient = client;
      this.addEntityDialogOpened = true;
    },
    openRemoveQuestionnaireDialog(questionnaire) {
      this.selectedQuestionnaire = questionnaire;
      if (questionnaire.opened_timestamp) {
        this.removeQuestionnaireConfirmationDialogOpened = true;
      } else {
        this.deleteQuestionnaire();
      }
    },
    async addEntity() {
      try {
        this.tableLoading = true;

        const response = await API.company.createCompany({
          userId: this.selectedClient.id,
          entityType: this.newType,
          firstName: this.newClientFirstName,
          lastName: this.newClientLastName,
          companyName:
            this.newType === 'Individual'
              ? this.newClientLastName + ', ' + this.newClientFirstName
              : this.newCompanyName,
          street: this.newEntityStreet,
          city: this.newEntityCity,
          state: this.newEntityState,
          country: this.newEntityCountry,
          zip: this.newEntityZip,
          businessEmail: this.newEntityBusinessEmail,
          otherEmail: this.newEntityOtherEmail,
          fax: this.newEntityFax,
        });

        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: `You've just added a new entity for ${this.selectedClient.full_name}!`,
          });
          await this.$store.dispatch('companies/getCompanies');

          this.refreshQuestionnaires();
        }
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
        console.log(e);
      } finally {
        this.newType = '';
        this.newClientFirstName = '';
        this.newClientLastName = '';
        this.newCompanyName = '';
        this.newEntityStreet = '';
        this.newEntityCity = '';
        this.newEntityState = '';
        this.newEntityCountry = '';
        this.newEntityZip = '';
        this.advancedClient = !!this.defaultAdvancedClient;
        this.newEntityBusinessEmail = '';
        this.newEntityOtherEmail = '';
        this.newEntityFax = '';
        this.addEntityDialogOpened = false;
        this.tableLoading = false;
      }
    },
    async refreshTable(shouldCallGetCompanies = true) {
      if (this.active === 2) {
        await this.getAllSignatureRequests();
      } else if (this.active === 3) {
        await this.getAllTaxReturns();
      } else if (this.active === 4) {
        await this.getAllActivities();
      } else if (this.active === 5) {
        await this.getAllTodoTasks();
      } else if (this.active === 6) {
        await this.getEntitiesWithTaxForms();
      } else {
        this.tableLoading = true;
        this.selectedQuestionnaires = [];

        if (shouldCallGetCompanies) {
          this.$store.dispatch('companies/getCompanies');
        }

        this.refreshQuestionnaires();
        this.$forceUpdate();
      }
    },

    async refreshClients() {
      this.tableLoading = true;
      await this.$store.dispatch('companies/getCompanies');
      this.$forceUpdate();
      this.tableLoading = false;
    },

    async refreshSignatureRequestsWithPolling(documentLocation) {
      let refreshInterval;

      const refreshTable = async () => {
        try {
          this.signatureRequestTableLoading = true;
          const response = await API.signatureRequest.getAllSignatureRequests();

          this.allSignatureRequests = response.data;

          if (
            this.allSignatureRequests.find(
              request => request.source === documentLocation,
            )
          ) {
            clearInterval(refreshInterval);
            this.signatureRequestTableLoading = false;

            this.refreshSignatureRequestsUntilAllProcessed();
          }
        } catch (error) {
          console.log(error);
        }
      };

      refreshInterval = setInterval(refreshTable, 2000);
    },

    async refreshSignatureRequestsUntilAllProcessed() {
      if (this.isPolling) {
        return;
      }

      let refreshInterval;
      this.isPolling = true;

      const refreshTable = async () => {
        try {
          const response = await API.signatureRequest.getAllSignatureRequests();

          this.allSignatureRequests = response.data;

          if (
            !this.allSignatureRequests.some(
              request => request.status === 'processing',
            )
          ) {
            clearInterval(refreshInterval);
            this.isPolling = false;
          }
        } catch (error) {
          console.log(error);
        }
      };

      refreshInterval = setInterval(refreshTable, 2000);
    },

    async refreshSignatureRequestsUntilSent(signatureRequestId) {
      let refreshInterval;

      const refreshTable = async () => {
        try {
          this.signatureRequestTableLoading = true;
          const response = await API.signatureRequest.getAllSignatureRequests();

          this.allSignatureRequests = response.data;

          const signatureRequest = this.allSignatureRequests.find(
            req => req.id === signatureRequestId,
          );

          if (signatureRequest && signatureRequest.status === 'sent') {
            clearInterval(refreshInterval);
            this.signatureRequestTableLoading = false;
          }
        } catch (error) {
          console.log(error);
        }
      };

      refreshInterval = setInterval(refreshTable, 2000);
    },

    async toggleLocked(questionnaire) {
      const response = await API.questionnaire.saveQuestionnaire({
        company_id: questionnaire.company_id,
        id: questionnaire.id,
        locked: !questionnaire.locked,
      });
      if (response.data.error) {
        console.log(response.data.error);
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
          errorToSend: response.data.error,
          userEmail: this.currentUser.email,
        });
      } else {
        if (questionnaire.locked) {
          this.$store.dispatch('application/setSuccessNotification', {
            message: "You've successfully unlocked the questionnaire.",
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: "You've successfully locked the questionnaire.",
          });
        }
        questionnaire.locked = !questionnaire.locked;
        questionnaire.status = 5;
        let cachedQuestionnaire = this.allQuestionnaires.find(
          q => q.id === response.data.id,
        );
        Object.assign(cachedQuestionnaire, response.data);
        this.$forceUpdate();
        this.lockQuestionnaireConfirmationDialogOpened = false;
        this.lockQuestionnaireAfterExportConfirmationDialogOpened = false;
      }
    },
    async deleteMultipleQuestionnaires() {
      this.tableLoading = true;
      this.deleteMultipleQuestionnairesConfirmationDialogOpened = false;
      this.selectedQuestionnaires.forEach(questionnaire => {
        API.questionnaire.deleteQuestionnaire({
          id: questionnaire.id,
          companyId: questionnaire.company_id,
        });
      });
      this.$store.dispatch('application/setSuccessNotification', {
        message: `You've deleted ${this.selectedQuestionnaires.length} questionnaires.`,
      });
      const deletedIds = this.selectedQuestionnaires.map(q => q.id);
      this.allQuestionnaires = this.allQuestionnaires.filter(
        q => !deletedIds.includes(q.id),
      );
      this.selectedQuestionnaires = [];
      this.tableLoading = false;
    },
    async deleteQuestionnaire() {
      this.tableLoading = true;
      try {
        this.removeQuestionnaireConfirmationDialogOpened = false;

        const response = await API.questionnaire.deleteQuestionnaire({
          id: this.selectedQuestionnaire.id,
          companyId: this.selectedQuestionnaire.company_id,
        });

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.refreshQuestionnaires();
          this.$store.dispatch('application/setSuccessNotification', {
            message: "You've successfully removed the questionnaire.",
          });
          this.allQuestionnaires = this.allQuestionnaires.filter(
            q => this.selectedQuestionnaire.id !== q.id,
          );
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.tableLoading = false;
      }
    },
    async deleteClient() {
      this.tableLoading = true;
      this.removeClientConfirmationDialogOpened = false;

      const response = await API.user.deleteClientFromAPI(
        this.selectedClient.id,
      );

      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        await this.$store.dispatch('getCurrentUser');
        await this.$store.dispatch('companies/getCompanies');
        this.refreshQuestionnaires();
        this.$store.dispatch('application/setSuccessNotification', {
          message: "You've successfully removed the client.",
        });
        this.$forceUpdate();
      }

      this.tableLoading = false;
    },
    async deleteEntity() {
      this.tableLoading = true;
      this.removeEntityConfirmationDialogOpened = false;

      const response = await API.company.deleteCompany(this.selectedEntity.id);

      if (response.data.error) {
        console.log(response.data.error);
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        await this.$store.dispatch('companies/getCompanies');
        this.refreshQuestionnaires();
        this.$store.dispatch('application/setSuccessNotification', {
          message: "You've successfully removed the entity.",
        });
        this.$forceUpdate();
      }
      this.tableLoading = false;
    },
    openRemoveClientConfirmationDialog(client) {
      this.selectedClient = client;
      this.removeClientConfirmationDialogOpened = true;
    },
    openRemoveEntityConfirmationDialog(entity) {
      this.selectedEntity = entity;
      this.removeEntityConfirmationDialogOpened = true;
    },
    async changeCurrentCompany(event, companyId, path) {
      event && event.preventDefault && event.preventDefault();

      await this.$store.dispatch('resetTiles', { companyId });
      this.$router.push(path);
    },
    async updateAssignments() {
      this.assignmentLoading = true;

      try {
        const response = await API.advisor.updateAssignments({
          ids: this.selectedClients.map(client => client.id),
          advisorId: this.selectedAssignedAdvisorId,
          cpaId: this.selectedCpaId,
          clientManagerId: this.selectedClientManagerId,
        });

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message:
              'These clients are currently being re-assigned. This may take a few minutes.',
          });
          this.selectedAssignedAdvisorId = null;
          this.selectedCpaId = null;
          this.selectedClientManagerId = null;
        }
        this.$store.dispatch('companies/getCompanies');
      } catch (e) {
        console.log(e);
      } finally {
        this.selectAssignmentDialogOpened = false;
        this.assignmentLoading = false;
      }
    },
    async updateAssignment(client, id) {
      try {
        const previouslyAssignedAdvisorId = client.assigned_advisor_id;
        const response = await API.advisor.updateAssignments({
          ids: [client.entity_type ? client.user.id : client.id],
          advisorId: id,
        });
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: `The newly assigned firm user has been added to all of this client's conversations, and replaced ${
              this.firmUsers.find(u => u.id === previouslyAssignedAdvisorId)
                .name
            } in the personal conversation.`,
          });
        }
        this.$store.dispatch('companies/getCompanies');
      } catch (e) {
        console.log(e);
      }
    },

    buildGetQuestionnairesParams() {
      const params = {
        year: this.selectedYear,
        page: this.questionnairePagination.page,
        rows_per_page: this.questionnairePagination.rowsPerPage,
        entity_type: this.entityFilter,
        questionnaire_type: this.questionnaireType,
        questionnaire_status: this.questionnaireStatus,
        questionnaire_status_include_locked: this
          .questionnaireStatusIncludeLocked,
        firm_group_id: this.selectedFirmGroup,
        sort_by: this.questionnairePagination.sortBy,
        descending: this.questionnairePagination.descending,
        search_term: this.questionnaireSearch,
        hide_inactives: this.hideInactive,
        assigned_advisor_id: this.selectedAssignedUser,
        selected_client_tags_filter: this.selectedClientTagsFilter,
        selected_entity_tags_filter: this.selectedEntityTagsFilter,
        selected_firm_custom_status_filter: this.selectedFirmCustomStatusFilter,
        selected_preparer_id: this.selectedPreparerUser,
        selected_client_manager_id: this.selectedClientManagerUser,
        sender_id: null,
      };

      if (this.selectedSender && this.selectedSender !== -1) {
        params.sender_id = this.selectedSender;
      }

      return params;
    },

    async refreshQuestionnaires() {
      this.tableLoading = true;
      this.refreshTries++;

      const params = this.buildGetQuestionnairesParams();

      try {
        // Cancel previous count request if it exists
        if (this.cancelTokenSourceCount) {
          this.cancelTokenSourceCount.cancel(
            'Cancelling previous count request.',
          );
        }
        this.cancelTokenSourceCount = axios.CancelToken.source();

        // 🔹 First request: Get total count
        const {
          data: { count },
        } = await axios.get('/v2/questionnaires', {
          params: { ...params, count: true },
          cancelToken: this.cancelTokenSourceCount.token,
        });

        this.totalQuestionnaires = count;

        // Cancel previous data request if it exists
        if (this.cancelTokenSourceData) {
          this.cancelTokenSourceData.cancel(
            'Cancelling previous data request.',
          );
        }
        this.cancelTokenSourceData = axios.CancelToken.source();

        // Second request: Get actual questionnaires for the page
        const response = await axios.get('/v2/questionnaires', {
          params,
          cancelToken: this.cancelTokenSourceData.token,
        });

        this.allQuestionnaires = response.data;

        if (
          this.allQuestionnaires.some(d => this.isLoading(d)) &&
          this.refreshTries < 10
        ) {
          await this.sleep(this.refreshTries * 1000);
          return this.refreshQuestionnaires();
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error fetching questionnaires:', error);
        }
      } finally {
        this.tableLoading = false;
      }
    },

    async downloadOverviewPageAsSpreadsheet() {
      const questionnaireIds = this.selectedQuestionnaires.map(q => q.id);

      try {
        const response = await API.advisor.downloadOverviewPageAsSpreadsheet({
          questionnaireIds,
        });

        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message:
              'Something went wrong with the download but we are looking into it.',
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message:
              'Your spreadsheet will be available shortly in your notifications.',
          });
        }
      } catch (err) {
        console.error(err);
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'Something went wrong with the download but we are looking into it.',
        });
      }
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    customEntityFilter(items, search) {
      const searchedItem = search.toLowerCase();

      return items.filter(row => {
        const entity_type = row.entity_type;

        let rowExists = true;
        if (searchedItem.length) {
          rowExists =
            row.name.toLowerCase().includes(searchedItem) ||
            (this.shouldShowExternalUserId
              ? row.external_user_id.toLowerCase().includes(searchedItem)
              : row.external_entity_id.toLowerCase().includes(searchedItem));
        }
        if (this.selectedAssignedUser !== -1 && rowExists) {
          rowExists = row.assigned_advisor_id === this.selectedAssignedUser;
        }
        if (this.entityFilter !== 'All' && rowExists) {
          rowExists = row.entity_type === this.entityFilter;
        }
        if (this.selectedFirmGroup !== -1 && rowExists) {
          rowExists =
            row.user.firm_group &&
            row.user.firm_group.id === this.selectedFirmGroup;
        }

        if (!isEmpty(this.selectedEntityTagsFilter) && rowExists) {
          if (this.selectedEntityTagsFilter.includes('(no tags)')) {
            if (
              isEmpty(
                this.selectedEntityTagsFilter.filter(a => a !== '(no tags)'),
              )
            ) {
              rowExists = isEmpty(row.entity_tags);
            } else {
              rowExists =
                this.selectedEntityTagsFilter
                  .filter(a => a !== '(no tags)')
                  .every(val =>
                    row.entity_tags.map(c => c.name).includes(val),
                  ) || isEmpty(row.entity_tags);
            }
          } else {
            rowExists = this.selectedEntityTagsFilter.every(val =>
              row.entity_tags.map(c => c.name).includes(val),
            );
          }
        }

        if (!isEmpty(this.selectedClientTagsFilter) && rowExists) {
          if (this.selectedClientTagsFilter.includes('(no tags)')) {
            if (
              isEmpty(
                this.selectedClientTagsFilter.filter(a => a !== '(no tags)'),
              )
            ) {
              rowExists = isEmpty(row.user.contact_tags);
            } else {
              rowExists =
                this.selectedClientTagsFilter
                  .filter(a => a !== '(no tags)')
                  .every(val =>
                    row.user.contact_tags.map(c => c.name).includes(val),
                  ) || isEmpty(row.user.contact_tags);
            }
          } else {
            rowExists = this.selectedClientTagsFilter.every(val =>
              row.contact_tags.map(c => c.name).includes(val),
            );
          }
        }

        if (this.entityType !== 'All' && rowExists) {
          rowExists = entity_type === this.entityType;
        }
        return rowExists;
      });
    },
    customUserFilter(items, search) {
      const searchedItem = search.toLowerCase();

      return items.filter(row => {
        const companyNames = row.companies.map(c => c.name.toLowerCase());
        const ClientTaxReturnName = row.search_name;
        const ClientName = row.full_name;
        const ClientEmail = row.email;
        let rowExists = true;
        if (searchedItem.length) {
          rowExists =
            companyNames.some(a => a.includes(searchedItem)) ||
            ClientTaxReturnName.toLowerCase().includes(searchedItem) ||
            ClientName.toLowerCase().includes(searchedItem) ||
            ClientEmail.toLowerCase().includes(searchedItem) ||
            (this.shouldShowExternalUserId
              ? row.external_user_id.toLowerCase().includes(searchedItem)
              : row.external_entity_id.toLowerCase().includes(searchedItem));
        }
        if (!isEmpty(this.selectedClientTagsFilter) && rowExists) {
          if (this.selectedClientTagsFilter.includes('(no tags)')) {
            if (
              isEmpty(
                this.selectedClientTagsFilter.filter(a => a !== '(no tags)'),
              )
            ) {
              rowExists = isEmpty(row.contact_tags);
            } else {
              rowExists =
                this.selectedClientTagsFilter
                  .filter(a => a !== '(no tags)')
                  .every(val =>
                    row.contact_tags.map(c => c.name).includes(val),
                  ) || isEmpty(row.contact_tags);
            }
          } else {
            rowExists = this.selectedClientTagsFilter.every(val =>
              row.contact_tags.map(c => c.name).includes(val),
            );
          }
        }
        if (this.selectedAssignedUser !== -1 && rowExists) {
          rowExists = row.assigned_advisor_id === this.selectedAssignedUser;
        }
        if (this.selectedFirmGroup !== -1 && rowExists) {
          rowExists =
            row.firm_group && row.firm_group.id === this.selectedFirmGroup;
        }
        return rowExists;
      });
    },
    async getAllFirmUsers() {
      const response = await API.firm.getAllUsers();
      if (response.data.error) {
      } else {
        this.firmUsers = response.data.users;
        this.firmUser = -1;
      }
    },
    async getCurrentUserFirm() {
      const response = await API.firm.getCurrentUserFirm();
      this.currentUserFirm = response.data;
      if (!isEmpty(this.currentUserFirm.tax_softwares)) {
        this.selectedTaxSoftware = this.currentUserFirm.tax_softwares[0];
      }
      if (this.defaultAdvancedClient) {
        this.advancedClient = true;
      }
    },
    async getAllEntityTypes() {
      const response = await API.firm.getAllEntityTypes();
      this.newTypes = uniq(
        defaultEntityTypes.concat(response.data.entity_types),
      );
    },
    async getAllQuestionnaireYears() {
      const response = await API.advisor.getAllQuestionnaireYears();
      this.allQuestionnaireYears = response.data.years;
    },
    async getAllActivities() {
      this.tableLoading = true;
      const response = await API.advisor.getAllActivities();

      this.allActivities = response.data;
      this.tableLoading = false;
    },
    async getAllTodoTasks() {
      this.tableLoading = true;
      const response = await axios.get('/todo_tasks');

      this.allTodoTasks = response.data;
      this.tableLoading = false;
    },
    async getAllSignatureRequests() {
      this.signatureRequestTableLoading = true;
      const response = await API.signatureRequest.getAllSignatureRequests();

      this.allSignatureRequests = response.data;
      this.signatureRequestTableLoading = false;

      if (
        this.allSignatureRequests.some(
          request => request.status === 'processing',
        )
      ) {
        this.refreshSignatureRequestsUntilAllProcessed();
      }
    },

    async getAllTaxReturns() {
      try {
        this.taxReturnDeliveryTableLoading = true;

        const response = await axios.get('/tax_returns/get_all_tax_returns', {
          params: {
            year: this.currentYear === 'All' ? null : this.currentYear,
          },
        });

        this.allTaxReturns = response.data;
      } catch (error) {
        console.log(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem loading the tax returns.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.taxReturnDeliveryTableLoading = false;
      }
    },

    async refreshTaxReturnsWithPolling(documentLocation) {
      let refreshInterval;

      const refreshTable = async () => {
        try {
          await this.getAllTaxReturns();

          if (
            this.allTaxReturns.find(
              request =>
                request.original_file === documentLocation &&
                request.status === 'action_required',
            )
          ) {
            clearInterval(refreshInterval);
          }
        } catch (error) {
          console.log(error);
        }
      };

      refreshInterval = setInterval(refreshTable, 2000);
    },

    async refreshTaxReturnsUntilSent(taxReturnId) {
      let refreshInterval;

      const refreshTable = async () => {
        try {
          await this.getAllTaxReturns();

          const taxReturn = this.allTaxReturns.find(
            req => req.id === taxReturnId,
          );

          if (taxReturn && taxReturn.status === 'sent') {
            clearInterval(refreshInterval);
          }
        } catch (error) {
          console.log(error);
        }
      };

      refreshInterval = setInterval(refreshTable, 2000);
    },

    async checkLinkedAccounts() {
      const response = await API.user.getLinkedAccounts();
      this.hasExpiredLinkedAccount = response.data.accounts.some(account => {
        return account.expired_at;
      });
    },

    async updateSelectedTasksStatus(newStatus) {
      try {
        this.tableLoading = true;
        await Promise.all(
          this.selectedTasks.map(task => {
            return axios.patch(`/todo_tasks/${task.id}`, {
              ...task,
              status: newStatus,
            });
          }),
        );

        await this.refreshTable();
      } finally {
        this.tableLoading = false;
      }
    },
    activateStep(incomingHash) {
      switch (incomingHash) {
        case '#onboarding-send-questionnaires':
          this.active = 0;
          this.isOnboarding = true;
          break;
        case '#onboarding-introduce-clients':
          this.active = 1;
          this.isOnboarding = true;
          break;
        case '#clients':
          this.active = 1;
          break;
        case '#signature-requests':
          this.active = 2;
          break;
        case '#tax-return-delivery':
          this.active = 3;
          break;
        case '#activities':
          this.active = 4;
          break;
        case '#tasks':
          this.active = 5;
          break;
        case '#review-export':
          this.active = 6;
          break;
        default:
          this.active = 0;
          this.isOnboarding = false;
          break;
      }
    },
    onActivitiesClick() {
      this.$router.replace('/overview#activities');
    },
    onTRDClick() {
      this.$router.replace('/overview#tax-return-delivery');
    },
    onTasksClick() {
      this.$router.replace('/overview#tasks');
    },
    onSignatureRequests() {
      this.$router.replace('/overview#signature-requests');
    },
    onClientsClick() {
      this.$router.replace('/overview#clients');
    },
    onOverviewClick() {
      this.$router.replace('/overview');
    },
    onReviewExportClick() {
      this.$router.replace('/overview#review-export');
    },
  },

  async created() {
    const { hash } = this.$route;
    this.activateStep(hash);

    try {
      this.refreshQuestionnairesWithDebounce = debounce(
        this.refreshQuestionnaires,
        500,
      );

      this.getEntitiesWithTaxFormsWithDebounce = debounce(
        this.getEntitiesWithTaxForms,
        500,
      );

      if (this.currentUser.overview_setting) {
        if (this.currentUser.overview_setting.show_clients_first) {
          this.active = 1;
        }
        this.selectedYear =
          this.currentUser.overview_setting.default_year === -1
            ? 'All'
            : this.currentUser.overview_setting.default_year;

        this.clientPagination.rowsPerPage =
          this.currentUser.overview_setting.all_clients_page_size || 10;
        this.entityPagination.rowsPerPage =
          this.currentUser.overview_setting.all_clients_page_size || 10;
        this.questionnairePagination.rowsPerPage =
          this.currentUser.overview_setting.all_questionnaires_page_size || 10;
      }

      this.$store.commit('application/START_LOADING');
      this.refreshTable(!this.companies || this.companies.length === 0);
      this.getAllFirmUsers();
      this.getCurrentUserFirm();
      this.getAllEntityTypes();
      this.getAllQuestionnaireYears();
      this.checkLinkedAccounts();

      if (this.$route.query.conversationId) {
        this.$emit(
          'openSpecificConversation',
          parseFloat(this.$route.query.conversationId),
        );
      }

      if (this.$route.query.questionnaireId && this.$route.query.companyId) {
        this.openQuestionnaireByCompanyIdAndQuestionnaireId(
          this.$route.query.companyId,
          this.$route.query.questionnaireId,
        );
      }

      // Reload stored filters
      this.checkStoredFilters();
    } catch (e) {
      console.log(e);
    } finally {
      this.$store.commit('application/END_LOADING');
      this.pageLoading = false;
    }
  },

  watch: {
    currentUser() {
      this.clientPagination.rowsPerPage =
        this.currentUser.overview_setting.all_clients_page_size || 10;
      this.entityPagination.rowsPerPage =
        this.currentUser.overview_setting.all_clients_page_size || 10;
      this.questionnairePagination.rowsPerPage =
        this.currentUser.overview_setting.all_questionnaires_page_size || 10;
    },

    companies: async function() {
      await this.getAllEntityTypes();
    },

    sendOpenItemsDialogOpened(val) {
      if (!val) {
        this.selectedOpenItemQuestionnaire = null;
      }
    },

    reviewAndExportYear: {
      async handler() {
        await this.refreshTable();
      },
    },

    reviewAndExportSearch: {
      async handler() {
        if (this.getEntitiesWithTaxFormsWithDebounce) {
          await this.getEntitiesWithTaxFormsWithDebounce();
        } else {
          await this.getEntitiesWithTaxForms();
        }
      },
    },

    reviewAndExportPagination: {
      async handler() {
        await this.refreshTable();
      },
    },

    $route: {
      async handler({ hash }) {
        this.activateStep(hash);

        if (this.$route.query.questionnaireId && this.$route.query.companyId) {
          this.openQuestionnaireByCompanyIdAndQuestionnaireId(
            this.$route.query.companyId,
            this.$route.query.questionnaireId,
          );
          this.$router.push('/overview');
        }
      },
    },

    active: {
      async handler(newValue) {
        await this.refreshTable();
        this.checkStoredFilters();
        console.log('active', newValue);
      },
    },
    clientView: {
      async handler(newValue) {
        this.checkStoredFilters();
        console.log('clientView', newValue);
      },
    },

    questionnairePagination: {
      async handler() {
        if (!this.tableLoading) {
          await this.refreshQuestionnaires();
        }
      },
    },

    selectedYear: {
      async handler() {
        await this.refreshQuestionnaires();
      },
    },

    hideInactive: {
      async handler() {
        await this.refreshQuestionnaires();
      },
    },

    selectedClientTagsFilter: {
      async handler() {
        await this.refreshQuestionnaires();
      },
    },

    selectedEntityTagsFilter: {
      async handler() {
        await this.refreshQuestionnaires();
      },
    },

    selectedFirmCustomStatusFilter: {
      async handler() {
        await this.refreshQuestionnaires();
      },
    },

    questionnaireStatusIncludeLocked: {
      async handler() {
        await this.refreshQuestionnaires();
      },
    },

    questionnaireStatus: {
      async handler() {
        await this.refreshQuestionnaires();
      },
    },

    questionnaireType: {
      async handler() {
        this.questionnairePagination.page = 1;
        await this.refreshQuestionnaires();
      },
    },

    entityFilter: {
      async handler() {
        this.questionnairePagination.page = 1;
        await this.refreshQuestionnaires();
      },
    },

    selectedAssignedUser: {
      async handler() {
        this.questionnairePagination.page = 1;
        await this.refreshQuestionnaires();
      },
    },

    selectedClientManagerUser: {
      async handler() {
        this.questionnairePagination.page = 1;
        await this.refreshQuestionnaires();
      },
    },

    selectedPreparerUser: {
      async handler() {
        this.questionnairePagination.page = 1;
        await this.refreshQuestionnaires();
      },
    },

    selectedSender: {
      async handler() {
        this.questionnairePagination.page = 1;
        await this.refreshQuestionnaires();
      },
    },

    selectedFirmGroup: {
      async handler() {
        this.questionnairePagination.page = 1;
        await this.refreshQuestionnaires();
      },
    },
  },
};
</script>

<style lang="css">
.client-panel {
  border: 1px solid #bdbdbd;
}
.client-name {
  text-decoration: none;
  color: #333;
}
.notification__text {
  color: #616161;
  font-size: 14px;
}
.notification__time {
  color: #757575;
}

.v-chip__content {
  cursor: pointer !important;
}
.outlined {
  border: solid 0.2px rgba(9, 30, 66, 0.06) !important;
}
.add-client-buttons {
  position: sticky;
  bottom: 0;
  background: #fff;
}
.drag-and-drop {
  height: 140px;
  border-radius: 4px;
  background: #eee;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.v-input--selection-controls__input + label {
  font-size: 14px;
  white-space: nowrap;
}

.overview__table__duedate {
  white-space: nowrap;
}

.overview__table__duedate--completed small {
  color: var(--gray-500) !important;
}

.overview__table__duedate--error,
.overview__table__duedate--warning {
  align-items: center;
  justify-content: left;
  display: flex;
  gap: 4px;
}

.overview__table__duedate--error,
.overview__table__duedate--error + small {
  color: var(--red-500);
}

.overview__table__duedate--warning,
.overview__table__duedate--warning + small {
  color: var(--orange-700);
}

.overview__table__duedate--warning svg {
  margin-top: -4px;
}

.overview__table__user {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  min-width: 24px;
  max-width: 24px;
  width: 24px;

  background-color: var(--blue-100);
  border-radius: 100%;
  color: var(--blue-600);
}

hr {
  border-color: var(--gray-300);
  border-top: 0 solid transparent;
}

.inactive-chip,
.readonly-chip {
  height: 20px !important;
}

.overview-table__external-id {
  color: var(--gray-700);

  word-break: break-all;
  text-decoration: none;
}

.overview-table__name {
  align-items: center;
  display: flex;
  gap: 6px;
  padding: 2px 0;

  text-align: left !important;
}

.overview-table__earliest-remind-date {
  color: var(--blue-600);
}

.overview-table__earliest-remind-date--empty {
  color: var(--gray-500);
}

.table-helper {
  display: flex;
  gap: 8px;
  justify-content: space-between;
  padding: 6px;

  background-color: var(--blue-100);
  border-radius: 6px;
}

.overview_dropdown_input {
  overflow: hidden;
  white-space: nowrap;
}

.include-locked-checkbox {
  position: sticky;
  bottom: -8px;

  padding: 8px 8px 16px;

  background-color: var(--gray-0);
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
  border-top: 1px solid var(--gray-200);
}

.filter-border--clear {
  border: 1px solid transparent;
  border-radius: 3px;
}

.filter-border--active {
  border: 1px solid var(--blue-400);
  border-radius: 3px;
}
</style>
